import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { usePaginatedProducts } from '../hooks/usePaginatedProducts';
import { scopedKey, getActiveAccountId } from '../utils/accountSession';
import { DASHBOARD_CARD_VISIBILITY_OPTIONS, SIDEBAR_VISIBILITY_OPTIONS } from '../constants/uiPreferencesOptions';
import { 
  Save, 
  Eye, 
  EyeOff, 
  Bot, 
  Settings,
  TestTube,
  UserCheck,
  Wrench,
  User,
  Plus,
  Edit2,
  Trash2,
  Database,
  Key,
  Shield,
  RefreshCw,
  Search
} from 'lucide-react';
import AiSearchConfigPanel from '../components/Configurations/AiSearchConfigPanel';
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialLight } from "react-syntax-highlighter/dist/esm/styles/prism";

const DEMO_KEY = 'demo-key-123';
const normalizeBaseIssuer = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    return new URL(raw).origin;
  } catch (error) {
    return raw.replace(/\/+$/, '');
  }
};
const SELF_API_ISSUER = normalizeBaseIssuer(API_BASE_URL);
const OAUTH_ISSUER_PRESETS = [
  { id: 'self', label: 'Self (This API)', issuer: SELF_API_ISSUER, defaultAudience: ['assistant-api'], jwksUrl: '', defaultAlgorithms: ['HS256'] },
  { id: 'custom', label: 'Custom issuer', issuer: '', defaultAudience: [], jwksUrl: '' },
  { id: 'auth0', label: 'Auth0', issuer: 'https://YOUR_DOMAIN/', defaultAudience: ['assistant-api'], jwksUrl: 'https://YOUR_DOMAIN/.well-known/jwks.json' },
  { id: 'azure-ad', label: 'Azure AD v2', issuer: 'https://login.microsoftonline.com/YOUR_TENANT_ID/v2.0', defaultAudience: ['api://assistant-api'], jwksUrl: 'https://login.microsoftonline.com/YOUR_TENANT_ID/discovery/v2.0/keys' },
  { id: 'okta', label: 'Okta Authorization Server', issuer: 'https://YOUR_OKTA_DOMAIN/oauth2/default', defaultAudience: ['api://default'], jwksUrl: 'https://YOUR_OKTA_DOMAIN/oauth2/default/v1/keys' },
  { id: 'cognito', label: 'AWS Cognito', issuer: 'https://cognito-idp.YOUR_REGION.amazonaws.com/YOUR_POOL_ID', defaultAudience: ['assistant-api'], jwksUrl: 'https://cognito-idp.YOUR_REGION.amazonaws.com/YOUR_POOL_ID/.well-known/jwks.json' }
];
const OAUTH_AUDIENCE_OPTIONS = ['assistant-api', 'api://assistant-api', 'api://default', 'openid', 'profile', 'email'];
const OAUTH_ALGORITHM_OPTIONS = ['RS256', 'RS384', 'RS512', 'HS256', 'HS384', 'HS512', 'ES256', 'ES384', 'ES512'];

const splitCsv = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
const joinCsv = (values) => values.join(', ');
const inferIssuerPreset = (issuer) => {
  const normalized = String(issuer || '').trim();
  if (!normalized) return 'custom';
  if (SELF_API_ISSUER && normalized === SELF_API_ISSUER) return 'self';
  if (normalized.includes('auth0')) return 'auth0';
  if (normalized.includes('login.microsoftonline.com')) return 'azure-ad';
  if (normalized.includes('okta')) return 'okta';
  if (normalized.includes('cognito-idp.')) return 'cognito';
  return 'custom';
};
const deriveJwksFromIssuer = (issuer) => {
  const raw = String(issuer || '').trim();
  if (!raw) return '';
  if (raw.includes('login.microsoftonline.com')) {
    const tenant = raw.split('login.microsoftonline.com/')[1]?.split('/')[0] || 'common';
    return `https://login.microsoftonline.com/${tenant}/discovery/v2.0/keys`;
  }
  if (raw.includes('/oauth2/default')) {
    return `${raw.replace(/\/$/, '')}/v1/keys`;
  }
  return `${raw.replace(/\/$/, '')}/.well-known/jwks.json`;
};

const sha256Hex = async (value) => {
  const content = String(value || '');
  if (!content || !window?.crypto?.subtle) {
    return '';
  }
  const encoder = new TextEncoder();
  const digest = await window.crypto.subtle.digest('SHA-256', encoder.encode(content));
  const bytes = Array.from(new Uint8Array(digest));
  return bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const Configurations = () => {
  const { getAuthHeaders } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('ai');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Account and Vector Store State
  const [accountInfo, setAccountInfo] = useState({ account_id: null });
  const [vectorStores, setVectorStores] = useState([]);
  const [defaultVectorStoreIndex, setDefaultVectorStoreIndex] = useState(null);
  const [showAddVectorStore, setShowAddVectorStore] = useState(false);
  const [editingVectorStore, setEditingVectorStore] = useState(null);
  const [vectorStoreForm, setVectorStoreForm] = useState({
    name: '',
    description: '',
    assistant: {
      name: '',
      instructions: '',
      model: ''
    }
  });
  const [creatingVectorStore, setCreatingVectorStore] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Separate configurations for different AI providers
  const [openaiConfig, setOpenaiConfig] = useState({
    provider: 'openai',
    openai_api_key: '',
    model: 'gpt-3.5-turbo',
    temperature: 0.7,
    max_tokens: 1000
  });
  
  const [anthropicConfig, setAnthropicConfig] = useState({
    provider: 'anthropic',
    api_key: '',
    model: 'claude-3-sonnet-20240229',
    temperature: 0.7,
    max_tokens: 1000
  });

  // Pinecone config
  const [pineconeConfig, setPineconeConfig] = useState({ api_key: '', index: '', environment: '' });

  // State for available models
  const [availableModels, setAvailableModels] = useState({
    openai: [],
    claude: []
  });
  const [loadingModels, setLoadingModels] = useState(false);
  
  const [preferredAIEngine, setPreferredAIEngine] = useState({
    preferred_engine: 'auto',
    description: 'System will automatically choose the best available AI engine'
  });
  const [openaiKeyConfigured, setOpenaiKeyConfigured] = useState(false);
  const [openaiKeyEditing, setOpenaiKeyEditing] = useState(false);

  // Guards to avoid overwriting OpenAI key after initial load or user interaction
  const openaiKeyTouched = useRef(false);
  const [, setDatabaseConfig] = useState({
    host: 'localhost',
    port: '5432',
    name: 'ai_tech_assistant',
    user: 'postgres',
    password: '',
    ssl_mode: 'prefer',
  });
  
  const [generalConfig, setGeneralConfig] = useState({
    app_name: 'AI Tech Assistant',
    chunk_size: 1000,
    chunk_overlap: 200,
    max_file_size: 10,
    rate_limit_per_minute: 60,
    branding: {
      logo_url: '',
      product_placeholder_url: ''
    },
    ui_preferences: {
      hidden_sidebar_item_ids: [],
      hidden_dashboard_card_ids: []
    }
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [placeholderUploading, setPlaceholderUploading] = useState(false);
  const resolveLogoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return `${API_BASE_URL}${url}`;
    return url;
  };

  // AI Agents Configuration
  const [aiSalesAgent, setAiSalesAgent] = useState({
    enabled: false,
    chime_in_time: 30, // seconds
    product_scope: 'all', // 'all' or 'selected'
    selected_products: [],
    caching_enabled: true,
    system_prompt: 'You are a helpful AI sales agent that assists customers with product information. Analyze the user\'s needs and provide relevant product recommendations based on available documentation and specifications. Always be friendly, informative, and focus on helping the customer find the right solution.'
  });

  const [aiTechAssistant, setAiTechAssistant] = useState({
    enabled: false,
    description: 'Answers any product related and technical support type questions based on the manuals and spec sheets of the product.',
    system_prompt: 'You are a technical support specialist AI assistant. Provide accurate, detailed technical information and troubleshooting guidance based on product manuals, specification sheets, and documentation. Focus on solving technical problems and answering specific technical questions with precision and clarity.'
  });

  // Paginated products hook
  const {
    selectedProductsData,
    availableProductsData,
    selectedProducts,
    selectedProductsPagination,
    availableProductsPagination,
    searchTerm,
    setSearchTerm,
    loadingSelected,
    loadingAvailable,
    initializeSelectedProducts,
    addProduct,
    removeProduct,
    clearAllProducts,
    selectAllFromCurrentPage,
    loadSelectedProductsPage,
    loadAvailableProductsPage
  } = usePaginatedProducts();
  
  // AI Agents vertical tab state
  const [activeAgentTab, setActiveAgentTab] = useState('sales-agent');

  // AI Sales Agent testing state
  const [testingSalesAgent, setTestingSalesAgent] = useState(false);
  const [testProductUrl, setTestProductUrl] = useState('https://www.herculesindustries.com/item/allied-commercial-zhb060s4bn-y');
  const [testSelectedProduct, setTestSelectedProduct] = useState('');
  const [testResults, setTestResults] = useState('');
  const [showTestResults, setShowTestResults] = useState(false);
  const [authKeys, setAuthKeys] = useState([]);
  const [authUsageEvents, setAuthUsageEvents] = useState([]);
  const [authUsageFilterKeyId, setAuthUsageFilterKeyId] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authActionLoading, setAuthActionLoading] = useState(false);
  const [newAuthKeyName, setNewAuthKeyName] = useState('');
  const [newAuthKeyDescription, setNewAuthKeyDescription] = useState('');
  const [newAuthKeyPlaintext, setNewAuthKeyPlaintext] = useState('');
  const [showAuthKeyModal, setShowAuthKeyModal] = useState(false);
  const [editingAuthKey, setEditingAuthKey] = useState(null);
  const [authKeyEditName, setAuthKeyEditName] = useState('');
  const [authKeyEditDescription, setAuthKeyEditDescription] = useState('');
  const [authKeyEditActive, setAuthKeyEditActive] = useState(true);
  const [oauthProviders, setOauthProviders] = useState([]);
  const [oauthClients, setOauthClients] = useState([]);
  const [oauthScopes, setOauthScopes] = useState([]);
  const [newScopeName, setNewScopeName] = useState('');
  const [newScopeDescription, setNewScopeDescription] = useState('');
  const [providerForm, setProviderForm] = useState({
    provider_name: '',
    issuer: '',
    audienceText: '',
    jwks_url: '',
    allowedAlgorithmsText: 'RS256',
    is_active: true
  });
  const [clientForm, setClientForm] = useState({
    client_name: '',
    client_id: '',
    client_secret: '',
    is_confidential: true,
    allowed_grants: ['client_credentials'],
    redirect_uris_text: '',
    default_scopes: []
  });
  const [createdClientSecret, setCreatedClientSecret] = useState('');
  const [diagnosticToken, setDiagnosticToken] = useState('');
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [oauth2Config, setOauth2Config] = useState({
    enabled: false,
    issuer: '',
    audienceText: '',
    jwks_url: '',
    algorithmsText: 'RS256',
    jwt_secret: '',
    clear_jwt_secret: false,
    has_jwt_secret: false
  });
  const [oauth2SelfHint, setOauth2SelfHint] = useState({
    issuer: '',
    secret_env_var: 'SECRET_KEY',
    secret_fingerprint: '',
    secret_source: ''
  });
  const [oauthIssuerPreset, setOauthIssuerPreset] = useState('custom');
  const [oauthAudienceCandidate, setOauthAudienceCandidate] = useState('');
  const [showOauthSecret, setShowOauthSecret] = useState(false);
  const [typedSecretFingerprint, setTypedSecretFingerprint] = useState('');
  const [selfSecretGeneratedValue, setSelfSecretGeneratedValue] = useState('');

  // useEffect calls will be placed after function declarations to avoid temporal dead zone

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const requestedTab = (params.get('tab') || '').trim().toLowerCase();
    if (!requestedTab) return;
    const allowed = new Set(['ai', 'agents', 'pinecone', 'ai_search', 'account', 'auth', 'general', 'ai_openai']);
    if (allowed.has(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);

  // Function to get token limits for different AI models
  const getModelTokenLimit = (modelName) => {
    if (!modelName) return null;
    
    const modelLower = modelName.toLowerCase();
    
    // OpenAI models
    if (modelLower.includes('gpt-5')) {
      return '8K tokens';
    } else if (modelLower.includes('gpt-4')) {
      return '128K tokens';
    } else if (modelLower.includes('gpt-3.5')) {
      return '16K tokens';
    }
    
    // Anthropic models
    if (modelLower.includes('claude-4')) {
      return '200K tokens';
    } else if (modelLower.includes('claude-3.5')) {
      return '200K tokens';
    } else if (modelLower.includes('claude-3')) {
      return '200K tokens';
    } else if (modelLower.includes('claude-2')) {
      return '100K tokens';
    }
    
    return null;
  };

  const formatModelDisplayName = (model) => {
    let displayName = model.id;
    
    // Add token limit if available
    const tokenLimit = getModelTokenLimit(model.id);
    if (tokenLimit) {
      displayName += ` (${tokenLimit})`;
    }
    
    // Add description if available
    if (model.description) {
      displayName += ` - ${model.description}`;
    }
    
    // Add creation date if available
    if (model.created) {
      const date = new Date(model.created * 1000);
      displayName += ` (${date.toLocaleDateString()})`;
    }
    
    return displayName;
  };

  const fetchAvailableModels = useCallback(async () => {
    try {
      setLoadingModels(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      // Fetch available models from both AI engines
      const [openaiResponse, claudeResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/v1/config/ai/models/openai`, { headers }),
        fetch(`${API_BASE_URL}/api/v1/config/ai/models/claude`, { headers })
      ]);

      if (openaiResponse.ok) {
        const openaiData = await openaiResponse.json();
        if (openaiData.available && openaiData.models) {
          setAvailableModels(prev => ({ ...prev, openai: openaiData.models }));
        }
      }

      if (claudeResponse.ok) {
        const claudeData = await claudeResponse.json();
        if (claudeData.available && claudeData.models) {
          setAvailableModels(prev => ({ ...prev, claude: claudeData.models }));
        }
      }
    } catch (error) {
      console.error('Error fetching available models:', error);
    } finally {
      setLoadingModels(false);
    }
  }, [getAuthHeaders]);



  const fetchConfigurations = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers || Object.keys(headers).length === 0) {
        console.warn('No auth headers available for fetching configurations');
        return;
      }
      console.log('Fetching configurations with headers:', headers);

      // Fetch AI configurations
      const aiResponse = await fetch(`${API_BASE_URL}/api/v1/config/ai`, { headers });
      if (aiResponse.ok) {
        const aiData = await aiResponse.json();
        console.log('AI Configuration data received:', aiData);
        if (aiData.openai) {
          const cfgRaw = aiData.openai.config || {};
          const serverKey = cfgRaw.api_key || cfgRaw.API_KEY || cfgRaw.key || '';
          const masked = serverKey ? `${serverKey.substring(0,4)}...${serverKey.substring(Math.max(0, serverKey.length-4))}` : '';
          console.log('OpenAI key from DB (raw):', serverKey);
          console.log('OpenAI key from DB (masked):', masked);
          const hasKey = !!(serverKey && serverKey !== DEMO_KEY);
          if (hasKey) {
            setOpenaiKeyConfigured(true);
          }
          setOpenaiConfig(prev => ({
            ...prev,
            provider: 'openai',
            openai_api_key: openaiKeyEditing
              ? prev.openai_api_key
              : (hasKey ? (openaiKeyTouched.current ? prev.openai_api_key : 'configured') : prev.openai_api_key),
            model: cfgRaw.model || prev.model || 'gpt-3.5-turbo',
            temperature: typeof cfgRaw.temperature === 'number' ? cfgRaw.temperature : (prev.temperature ?? 0.7),
            max_tokens: typeof cfgRaw.max_tokens === 'number' ? cfgRaw.max_tokens : (prev.max_tokens ?? 1000),
          }));
          console.log('OpenAI config updated:', aiData.openai);
        }
        if (aiData.anthropic) {
          const cfgA = aiData.anthropic.config || {};
          setAnthropicConfig(prev => ({
            ...prev,
            provider: 'anthropic',
            api_key: cfgA.api_key || prev.api_key || '',
            model: cfgA.model || prev.model || 'claude-3-sonnet-20240229',
            temperature: typeof cfgA.temperature === 'number' ? cfgA.temperature : (prev.temperature ?? 0.7),
            max_tokens: typeof cfgA.max_tokens === 'number' ? cfgA.max_tokens : (prev.max_tokens ?? 1000),
          }));
          console.log('Anthropic config updated:', aiData.anthropic);
        }
      } else {
        console.error('Failed to fetch AI configurations:', aiResponse.status, await aiResponse.text());
      }

      // Fetch database configuration
      const dbResponse = await fetch(`${API_BASE_URL}/api/v1/config/database`, { headers });
      if (dbResponse.ok) {
        const data = await dbResponse.json();
        console.log('Database configuration data received:', data);
        if (data.config) {
          setDatabaseConfig(prev => ({ ...prev, ...data.config }));
          console.log('Database config updated:', data.config);
        }
      }

      // Fetch preferred AI engine configuration
      const preferredResponse = await fetch(`${API_BASE_URL}/api/v1/config/ai/preferred`, { headers });
      if (preferredResponse.ok) {
        const data = await preferredResponse.json();
        console.log('Preferred AI configuration data received:', data);
        // Extract only the configuration fields, exclude updated_at
        const { updated_at, ...config } = data;
        setPreferredAIEngine(prev => ({ ...prev, ...config }));
        console.log('Preferred AI config updated:', config);
      }

      // Fetch general configuration
      const generalResponse = await fetch(`${API_BASE_URL}/api/v1/config/general`, { headers });
      if (generalResponse.ok) {
        const data = await generalResponse.json();
        console.log('General configuration data received:', data);
        if (data.config) {
          setGeneralConfig(prev => ({ ...prev, ...data.config }));
          console.log('General config updated:', data.config);
        }
      }

      // Fetch AI agents configuration
      const agentsResponse = await fetch(`${API_BASE_URL}/api/v1/config/agents`, { headers });
      if (agentsResponse.ok) {
        const data = await agentsResponse.json();
        console.log('AI Agents configuration data received:', data);
        if (data.sales_agent) {
          setAiSalesAgent(prev => ({ ...prev, ...data.sales_agent }));
          // Initialize selected products from the configuration
          if (data.sales_agent.selected_products) {
            initializeSelectedProducts(data.sales_agent.selected_products);
          }
        }
        if (data.tech_assistant) {
          setAiTechAssistant(prev => ({ ...prev, ...data.tech_assistant }));
        }
      }

    } catch (error) {
      console.error('Error fetching configurations:', error);
    }
  }, [getAuthHeaders, initializeSelectedProducts, openaiKeyEditing]);

  // useEffect hooks - placed after function declarations to avoid temporal dead zone
  useEffect(() => {
    fetchConfigurations();
    fetchAvailableModels();
    // Load pinecone config
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/config/pinecone`, { headers: getAuthHeaders() });
        if (response.ok) {
          const data = await response.json();
          setPineconeConfig({
            api_key: data.config?.api_key || '',
            index: data.config?.index || '',
            environment: data.config?.environment || data.config?.host || ''
          });
        }
      } catch (e) {
        console.error('Failed to load Pinecone config', e);
      }
    })();
  }, [fetchConfigurations, fetchAvailableModels, getAuthHeaders]);

  // Initialize test product selection when sales agent config changes
  useEffect(() => {
    if (aiSalesAgent.selected_products && aiSalesAgent.selected_products.length > 0 && !testSelectedProduct) {
      setTestSelectedProduct(aiSalesAgent.selected_products[0]);
    }
  }, [aiSalesAgent.selected_products, testSelectedProduct]);

  // Note: Database configuration is currently hidden - function commented out to avoid ESLint warnings
  // const handleDatabaseSave = async () => {
  //   try {
  //     setSaving(true);
  //     const response = await fetch(`${API_BASE_URL}/api/v1/config/database`, {
  //       method: 'PUT',
  //       headers: {
  //         ...getAuthHeaders(),
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify(databaseConfig)
  //     });

  //     if (response.ok) {
  //       alert('Database configuration saved successfully!');
  //       // Refresh configurations after successful save
  //       fetchConfigurations();
  //     } else {
  //       const errorData = await response.text();
  //       console.error('Save failed:', response.status, errorData);
  //       alert(`Failed to save Database configuration: ${response.status} - ${errorData}`);
  //     }
  //   } catch (error) {
  //     console.error('Error saving database configuration:', error);
  //     alert('Failed to save database configuration');
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  const handleOpenAISave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/ai`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'openai',
          api_key: openaiConfig.openai_api_key,
          model: openaiConfig.model,
          temperature: openaiConfig.temperature,
          max_tokens: openaiConfig.max_tokens
        })
      });

      if (response.ok) {
        alert('OpenAI configuration saved successfully!');
        setOpenaiKeyConfigured(true);
        setOpenaiKeyEditing(false);
        openaiKeyTouched.current = false;
        setOpenaiConfig((prev) => ({
          ...prev,
          openai_api_key: prev.openai_api_key || 'configured',
        }));
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save OpenAI configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving OpenAI configuration:', error);
      alert('Failed to save OpenAI configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAnthropicSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/ai`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({...anthropicConfig, type: 'anthropic'})
      });

      if (response.ok) {
        alert('Anthropic configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save Anthropic configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving Anthropic configuration:', error);
      alert('Failed to save Anthropic configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneralSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/general`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(generalConfig)
      });

      if (response.ok) {
        alert('General configuration saved successfully!');
        try {
          const payload = generalConfig?.ui_preferences || {};
          localStorage.setItem(scopedKey('ui_preferences', getActiveAccountId()), JSON.stringify(payload));
          window.dispatchEvent(new CustomEvent('ui-preferences-updated', { detail: payload }));
        } catch (err) {
          // non-blocking cache update
        }
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save General configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving general configuration:', error);
      alert('Failed to save general configuration');
    } finally {
      setSaving(false);
    }
  };


  const toggleHiddenPreference = (key, value, shouldShow) => {
    setGeneralConfig((prev) => {
      const currentPreferences = prev.ui_preferences || {};
      const currentHidden = Array.isArray(currentPreferences[key]) ? currentPreferences[key] : [];
      const nextHidden = shouldShow
        ? currentHidden.filter((item) => item !== value)
        : Array.from(new Set([...currentHidden, value]));
      return {
        ...prev,
        ui_preferences: {
          ...currentPreferences,
          [key]: nextHidden,
        },
      };
    });
  };

  const handleLogoUpload = async (file) => {
    try {
      setLogoUploading(true);
      const headers = getAuthHeaders();
      if (!headers || Object.keys(headers).length === 0) {
        console.warn('No auth headers available for logo upload');
        return;
      }
      const uploadHeaders = { ...headers };
      delete uploadHeaders['Content-Type'];
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/general/logo`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setGeneralConfig((prev) => ({
          ...prev,
          branding: {
            ...(prev.branding || {}),
            logo_url: data.logo_url || '',
          },
        }));
        alert('Logo uploaded successfully!');
      } else {
        const errorData = await response.text();
        alert(`Failed to upload logo: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      alert('Logo upload failed');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleProductPlaceholderUpload = async (file) => {
    try {
      setPlaceholderUploading(true);
      const headers = getAuthHeaders();
      if (!headers || Object.keys(headers).length === 0) {
        console.warn('No auth headers available for placeholder upload');
        return;
      }
      const uploadHeaders = { ...headers };
      delete uploadHeaders['Content-Type'];
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/general/product-placeholder`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setGeneralConfig((prev) => ({
          ...prev,
          branding: {
            ...(prev.branding || {}),
            product_placeholder_url: data.product_placeholder_url || '',
          },
        }));
        alert('Product placeholder uploaded successfully!');
      } else {
        const errorData = await response.text();
        alert(`Failed to upload placeholder: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Placeholder upload error:', error);
      alert('Product placeholder upload failed');
    } finally {
      setPlaceholderUploading(false);
    }
  };

  // Note: Database configuration is currently hidden - function commented out to avoid ESLint warnings
  // const testDatabaseConnection = async () => {
  //   try {
  //     const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
  //       headers: getAuthHeaders()
  //     });
  //     if (response.ok) {
  //       const data = await response.json();
  //       if (data.services?.database?.status === 'healthy') {
  //         alert('Database connection successful!');
  //       } else {
  //         alert('Database connection failed. Check your configuration.');
  //       }
  //     }
  //   } catch (error) {
  //     alert('Failed to test database connection');
  //   }
  // };

  const testAIConnection = async (provider) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        alert('Authentication required. Please log in again.');
        return;
      }

      // Get the current configuration for the provider
      let config;
      let model;
      if (provider === 'OpenAI') {
        config = openaiConfig;
        model = openaiConfig.model;
      } else if (provider === 'Anthropic') {
        config = anthropicConfig;
        model = anthropicConfig.model;
      } else {
        alert(`Unknown provider: ${provider}`);
        return;
      }

      // Check if API key is provided
      if (!config.api_key || config.api_key.trim() === '' || config.api_key === DEMO_KEY) {
        alert(`${provider} API key is required. Please enter a valid API key first.`);
        return;
      }

      // Check if model is selected
      if (!model || model.trim() === '') {
        alert(`${provider} model is required. Please select a valid model first.`);
        return;
      }

      // Show testing message
      const keyPreview =
        provider === 'OpenAI'
          ? (openaiConfig.openai_api_key || '').trim().substring(0, 8)
          : (anthropicConfig.api_key || '').trim().substring(0, 8);
      alert(`Testing ${provider} connection...\n\nAPI Key: ${keyPreview}...\nModel: ${model}\n\nThis will validate both the API key and model name.`);

      // Make a real API call to test the configuration
      const response = await fetch(`${API_BASE_URL}/api/v1/config/ai/test`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          engine_type: provider.toLowerCase(),
          api_key: (provider === 'OpenAI' ? openaiConfig.openai_api_key : anthropicConfig.api_key)?.trim(),
          model_name: model?.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert(`✅ ${provider} connection test successful!\n\nAPI Key: Valid ✅\nModel: ${model} ✅\nResponse: ${data.message}\n\nYour ${provider} configuration is working correctly.`);
        } else {
          alert(`❌ ${provider} connection test failed!\n\nAPI Key: ${data.api_key_valid ? 'Valid ✅' : 'Invalid ❌'}\nModel: ${data.model_valid ? 'Valid ✅' : 'Invalid ❌'}\n\nError: ${data.message}\n\nPlease check your configuration and try again.`);
        }
      } else {
        const errorData = await response.json();
        alert(`❌ ${provider} connection test failed!\n\nServer Error: ${response.status}\nMessage: ${errorData.detail || 'Unknown error'}\n\nPlease check your configuration and try again.`);
      }
    } catch (error) {
      console.error(`Error testing ${provider} connection:`, error);
      alert(`❌ ${provider} connection test failed!\n\nError: ${error.message}\n\nPlease check your configuration and try again.`);
    }
  };

  const handlePreferredAISave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/ai/preferred`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferredAIEngine)
      });

      if (response.ok) {
        alert('Preferred AI engine configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save Preferred AI engine configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving preferred AI engine configuration:', error);
      alert('Error saving preferred AI engine configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSalesAgentSave = async () => {
    try {
      setSaving(true);
      // Get current tech assistant config to preserve it
      const currentConfigResponse = await fetch(`${API_BASE_URL}/api/v1/config/agents`, {
        headers: getAuthHeaders()
      });
      
      let currentTechAssistant = aiTechAssistant;
      if (currentConfigResponse.ok) {
        const currentConfig = await currentConfigResponse.json();
        currentTechAssistant = currentConfig.tech_assistant || aiTechAssistant;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/config/agents`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sales_agent: {
            ...aiSalesAgent,
            selected_products: selectedProducts
          },
          tech_assistant: currentTechAssistant
        })
      });

      if (response.ok) {
        alert('AI Sales Agent configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save AI Sales Agent configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving AI Sales Agent configuration:', error);
      alert('Error saving AI Sales Agent configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTechAssistantSave = async () => {
    try {
      setSaving(true);
      // Get current sales agent config to preserve it
      const currentConfigResponse = await fetch(`${API_BASE_URL}/api/v1/config/agents`, {
        headers: getAuthHeaders()
      });
      
      let currentSalesAgent = aiSalesAgent;
      if (currentConfigResponse.ok) {
        const currentConfig = await currentConfigResponse.json();
        currentSalesAgent = currentConfig.sales_agent || aiSalesAgent;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/config/agents`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sales_agent: currentSalesAgent,
          tech_assistant: aiTechAssistant
        })
      });

      if (response.ok) {
        alert('AI Tech Assistant configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save AI Tech Assistant configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving AI Tech Assistant configuration:', error);
      alert('Error saving AI Tech Assistant configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSalesAgent = async () => {
    if (!testProductUrl.trim()) {
      alert('Please enter a product URL');
      return;
    }
    
    if (!testSelectedProduct) {
      alert('Please select a product');
      return;
    }

    try {
      setTestingSalesAgent(true);
      setTestResults('');
      setShowTestResults(true);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/ai-sales-assistant`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productUrl: testProductUrl,
          productCode: testSelectedProduct,
          accountId: '1'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedResults = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              setTestResults(accumulatedResults);
              return;
            }
            accumulatedResults += data;
            setTestResults(accumulatedResults);
          }
        }
      }
    } catch (error) {
      console.error('Error testing AI Sales Agent:', error);
      setTestResults(`Error: ${error.message}`);
    } finally {
      setTestingSalesAgent(false);
    }
  };

  // Product selector utility functions

  // Account and Vector Store Functions
  const fetchAccountInfo = useCallback(async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/config/account/info`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setAccountInfo(data);
      }
    } catch (error) {
      console.error('Failed to fetch account info:', error);
    }
  }, [getAuthHeaders]);

  const fetchVectorStores = useCallback(async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setVectorStores(data.vector_stores || []);
        setDefaultVectorStoreIndex(data.default_vector_store_index);
      }
    } catch (error) {
      console.error('Failed to fetch vector stores:', error);
    }
  }, [getAuthHeaders]);

  const handleCreateVectorStore = async () => {
    if (!vectorStoreForm.name.trim()) return;
    
    // Check vector store limit before attempting creation
    if (vectorStores.length >= (accountInfo.max_vector_store || 1)) {
      alert(`Maximum vector store limit reached (${accountInfo.max_vector_store || 1}). Cannot create more vector stores.`);
      return;
    }
    
    setCreatingVectorStore(true);
    try {
      const authHeaders = getAuthHeaders();
      
      // Prepare the request data with assistant configuration
      const requestData = {
        name: vectorStoreForm.name,
        description: vectorStoreForm.description,
        assistant: {
          name: vectorStoreForm.assistant.name || null,
          instructions: vectorStoreForm.assistant.instructions || null,
          model: vectorStoreForm.assistant.model || null
        }
      };
      
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        await fetchVectorStores(); // Refresh the list
        handleCloseVectorStoreModal();
        alert(result.message || 'Vector store and assistant created successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to create vector store: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating vector store:', error);
      alert('Failed to create vector store. Please try again.');
    } finally {
      setCreatingVectorStore(false);
    }
  };

  const handleEditVectorStore = (store, index) => {
    setEditingVectorStore({ ...store, index });
    setVectorStoreForm({
      name: store.name,
      description: store.description,
      assistant: {
        name: store.assistant_name || '',
        instructions: store.assistant_instructions || '',
        model: store.assistant_model || ''
      }
    });
  };

  const handleUpdateVectorStore = async () => {
    if (!vectorStoreForm.name.trim() || !editingVectorStore) return;
    
    setCreatingVectorStore(true);
    try {
      const authHeaders = getAuthHeaders();
      
      // Prepare the request data with assistant configuration
      const requestData = {
        name: vectorStoreForm.name,
        description: vectorStoreForm.description,
        assistant: {
          name: vectorStoreForm.assistant.name || null,
          instructions: vectorStoreForm.assistant.instructions || null,
          model: vectorStoreForm.assistant.model || null
        }
      };
      
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores/update/${editingVectorStore.index}`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        await fetchVectorStores(); // Refresh the list
        handleCloseVectorStoreModal();
        alert('Vector store and assistant updated successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to update vector store: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating vector store:', error);
      alert('Failed to update vector store. Please try again.');
    } finally {
      setCreatingVectorStore(false);
    }
  };

  const handleDeleteVectorStore = (index) => {
    setDeleteTarget(index);
    setShowDeleteConfirm(true);
  };

  const handleSetDefaultVectorStore = async (index) => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores-default`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vector_store_index: index })
      });

      if (response.ok) {
        setDefaultVectorStoreIndex(index);
        // You could add a success notification here
      } else {
        const errorData = await response.json();
        console.error('Failed to set default vector store:', errorData);
        alert(errorData.detail || 'Failed to set default vector store');
      }
    } catch (error) {
      console.error('Error setting default vector store:', error);
      alert('Error setting default vector store');
    }
  };

  const confirmDeleteVectorStore = async () => {
    if (deleteTarget === null) return;

    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores/${deleteTarget}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        await fetchVectorStores(); // Refresh the list
        alert('Vector store deleted successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to delete vector store: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting vector store:', error);
      alert('Failed to delete vector store. Please try again.');
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const cancelDeleteVectorStore = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleCloseVectorStoreModal = () => {
    setShowAddVectorStore(false);
    setEditingVectorStore(null);
    setVectorStoreForm({
      name: '',
      description: '',
      assistant: {
        name: '',
        instructions: '',
        model: ''
      }
    });
  };

  const fetchAuthKeys = useCallback(async () => {
    const headers = getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/keys`, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to load auth keys: ${response.status} ${errorText}`);
    }
    const data = await response.json();
    setAuthKeys(Array.isArray(data.keys) ? data.keys : []);
  }, [getAuthHeaders]);

  const fetchOAuth2Config = useCallback(async () => {
    const headers = getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/oauth2`, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to load OAuth2 config: ${response.status} ${errorText}`);
    }
    const data = await response.json();
    const oauth = data.oauth2 || {};
    const selfHint = data.self_hint || {};
    const issuer = oauth.issuer || '';
    setOauth2Config({
      enabled: !!oauth.enabled,
      issuer,
      audienceText: Array.isArray(oauth.audience) ? oauth.audience.join(', ') : '',
      jwks_url: oauth.jwks_url || '',
      algorithmsText: Array.isArray(oauth.algorithms) && oauth.algorithms.length > 0 ? oauth.algorithms.join(', ') : 'RS256',
      jwt_secret: '',
      clear_jwt_secret: false,
      has_jwt_secret: !!oauth.has_jwt_secret
    });
    setOauthIssuerPreset(inferIssuerPreset(issuer));
    setOauth2SelfHint({
      issuer: selfHint.issuer || '',
      secret_env_var: selfHint.secret_env_var || 'SECRET_KEY',
      secret_fingerprint: selfHint.secret_fingerprint || '',
      secret_source: selfHint.secret_source || ''
    });
  }, [getAuthHeaders]);

  const fetchAuthUsage = useCallback(async (filterKeyId = '') => {
    const headers = getAuthHeaders();
    const query = filterKeyId ? `?key_id=${encodeURIComponent(filterKeyId)}&limit=200` : '?limit=200';
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/keys/usage${query}`, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to load auth usage: ${response.status} ${errorText}`);
    }
    const data = await response.json();
    setAuthUsageEvents(Array.isArray(data.events) ? data.events : []);
  }, [getAuthHeaders]);

  const handleCreateAuthKey = async () => {
    if (!newAuthKeyName.trim()) {
      alert('Key name is required');
      return;
    }
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/keys`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newAuthKeyName.trim(),
          description: newAuthKeyDescription.trim()
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create auth key: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      setNewAuthKeyPlaintext(data.api_key || '');
      setNewAuthKeyName('');
      setNewAuthKeyDescription('');
      await fetchAuthKeys();
      await fetchAuthUsage(authUsageFilterKeyId);
    } catch (error) {
      console.error('Failed to create auth key:', error);
      alert('Failed to create auth key');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const openAuthKeyEditModal = (keyItem) => {
    setEditingAuthKey(keyItem);
    setAuthKeyEditName(keyItem?.name || '');
    setAuthKeyEditDescription(keyItem?.description || '');
    setAuthKeyEditActive(!!keyItem?.is_active);
    setShowAuthKeyModal(true);
  };

  const closeAuthKeyEditModal = () => {
    setShowAuthKeyModal(false);
    setEditingAuthKey(null);
    setAuthKeyEditName('');
    setAuthKeyEditDescription('');
    setAuthKeyEditActive(true);
  };

  const handleSaveAuthKeyMeta = async () => {
    if (!editingAuthKey?.id) return;
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/keys/${editingAuthKey.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: authKeyEditName.trim(),
          description: authKeyEditDescription.trim(),
          is_active: !!authKeyEditActive
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update auth key: ${response.status} ${errorText}`);
      }
      closeAuthKeyEditModal();
      await fetchAuthKeys();
      await fetchAuthUsage(authUsageFilterKeyId);
    } catch (error) {
      console.error('Failed to update auth key:', error);
      alert('Failed to update auth key');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleRotateAuthKey = async (keyId) => {
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/keys/${keyId}/rotate`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to rotate auth key: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      if (data.api_key) {
        setNewAuthKeyPlaintext(data.api_key);
      }
      await fetchAuthKeys();
    } catch (error) {
      console.error('Failed to rotate auth key:', error);
      alert('Failed to rotate auth key');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleDeactivateAuthKey = async (keyId) => {
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/keys/${keyId}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to deactivate auth key: ${response.status} ${errorText}`);
      }
      await fetchAuthKeys();
      await fetchAuthUsage(authUsageFilterKeyId);
    } catch (error) {
      console.error('Failed to deactivate auth key:', error);
      alert('Failed to deactivate auth key');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleSaveOAuth2Config = async () => {
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const enforcedAlgorithms = isSelfPreset ? ['HS256'] : splitCsv(oauth2Config.algorithmsText);
      const enforcedJwksUrl = isSelfPreset ? '' : oauth2Config.jwks_url.trim();
      const payload = {
        enabled: !!oauth2Config.enabled,
        issuer: oauth2Config.issuer.trim(),
        audience: splitCsv(oauth2Config.audienceText),
        jwks_url: enforcedJwksUrl,
        algorithms: enforcedAlgorithms,
        jwt_secret: oauth2Config.jwt_secret,
        clear_jwt_secret: !!oauth2Config.clear_jwt_secret
      };
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/oauth2`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save OAuth2 config: ${response.status} ${errorText}`);
      }
      await fetchOAuth2Config();
      alert('OAuth2 configuration saved');
    } catch (error) {
      console.error('Failed to save OAuth2 config:', error);
      alert('Failed to save OAuth2 configuration');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleGenerateSelfSigningSecret = async () => {
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/oauth2/self-secret/generate`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }
      const data = await response.json();
      setSelfSecretGeneratedValue(data.jwt_secret || '');
      setOauth2Config((prev) => ({ ...prev, jwt_secret: data.jwt_secret || prev.jwt_secret, clear_jwt_secret: false }));
      await fetchOAuth2Config();
    } catch (error) {
      console.error('Failed to generate self signing secret', error);
      alert('Failed to generate self signing secret');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleRotateSelfSigningSecret = async () => {
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/oauth2/self-secret/rotate`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ revoke_active_tokens: true })
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }
      const data = await response.json();
      setSelfSecretGeneratedValue(data.jwt_secret || '');
      setOauth2Config((prev) => ({ ...prev, jwt_secret: data.jwt_secret || prev.jwt_secret, clear_jwt_secret: false }));
      await fetchOAuth2Config();
      alert(`Self secret rotated. Revoked active tokens: ${data.revoked_active_tokens || 0}`);
    } catch (error) {
      console.error('Failed to rotate self signing secret', error);
      alert('Failed to rotate self signing secret');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const fetchOauthProviders = useCallback(async () => {
    const headers = getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/providers`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch providers: ${response.status}`);
    }
    const data = await response.json();
    setOauthProviders(Array.isArray(data.providers) ? data.providers : []);
  }, [getAuthHeaders]);

  const fetchOauthClients = useCallback(async () => {
    const headers = getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/clients`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch clients: ${response.status}`);
    }
    const data = await response.json();
    setOauthClients(Array.isArray(data.clients) ? data.clients : []);
  }, [getAuthHeaders]);

  const fetchOauthScopes = useCallback(async () => {
    const headers = getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/scopes`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to fetch scopes: ${response.status}`);
    }
    const data = await response.json();
    setOauthScopes(Array.isArray(data.scopes) ? data.scopes : []);
  }, [getAuthHeaders]);

  const loadAuthSettings = useCallback(async () => {
    try {
      setAuthLoading(true);
      await Promise.all([
        fetchAuthKeys(),
        fetchOAuth2Config(),
        fetchAuthUsage(authUsageFilterKeyId),
        fetchOauthProviders(),
        fetchOauthClients(),
        fetchOauthScopes()
      ]);
    } catch (error) {
      console.error('Failed to load auth settings:', error);
      alert('Failed to load account authentication settings');
    } finally {
      setAuthLoading(false);
    }
  }, [authUsageFilterKeyId, fetchAuthKeys, fetchOAuth2Config, fetchAuthUsage, fetchOauthProviders, fetchOauthClients, fetchOauthScopes]);

  const handleCreateScope = async () => {
    if (!newScopeName.trim()) return;
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/scopes`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope_name: newScopeName.trim(),
          description: newScopeDescription.trim()
        })
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }
      setNewScopeName('');
      setNewScopeDescription('');
      await fetchOauthScopes();
      await fetchOauthClients();
    } catch (error) {
      console.error('Failed to create scope', error);
      alert('Failed to create scope');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleCreateProvider = async () => {
    if (!providerForm.provider_name.trim() || !providerForm.issuer.trim()) return;
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/providers`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider_name: providerForm.provider_name.trim(),
          issuer: providerForm.issuer.trim(),
          audience: splitCsv(providerForm.audienceText),
          jwks_url: providerForm.jwks_url.trim(),
          allowed_algorithms: splitCsv(providerForm.allowedAlgorithmsText),
          is_active: !!providerForm.is_active
        })
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }
      setProviderForm({
        provider_name: '',
        issuer: '',
        audienceText: '',
        jwks_url: '',
        allowedAlgorithmsText: 'RS256',
        is_active: true
      });
      await fetchOauthProviders();
    } catch (error) {
      console.error('Failed to create provider', error);
      alert('Failed to create provider');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleDeleteProvider = async (providerId) => {
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/providers/${providerId}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }
      await fetchOauthProviders();
    } catch (error) {
      console.error('Failed to delete provider', error);
      alert('Failed to delete provider');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const toggleClientGrant = (grant) => {
    setClientForm((prev) => {
      const current = Array.isArray(prev.allowed_grants) ? prev.allowed_grants : [];
      if (current.includes(grant)) {
        return { ...prev, allowed_grants: current.filter((item) => item !== grant) };
      }
      return { ...prev, allowed_grants: [...current, grant] };
    });
  };

  const toggleClientScope = (scopeName) => {
    setClientForm((prev) => {
      const current = Array.isArray(prev.default_scopes) ? prev.default_scopes : [];
      if (current.includes(scopeName)) {
        return { ...prev, default_scopes: current.filter((item) => item !== scopeName) };
      }
      return { ...prev, default_scopes: [...current, scopeName] };
    });
  };

  const handleCreateClient = async () => {
    if (!clientForm.client_name.trim()) return;
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/clients`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: clientForm.client_name.trim(),
          client_id: clientForm.client_id.trim(),
          client_secret: clientForm.client_secret,
          is_confidential: !!clientForm.is_confidential,
          allowed_grants: clientForm.allowed_grants,
          redirect_uris: splitCsv(clientForm.redirect_uris_text),
          default_scopes: clientForm.default_scopes
        })
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }
      const data = await response.json();
      setCreatedClientSecret(data.client_secret || '');
      setClientForm({
        client_name: '',
        client_id: '',
        client_secret: '',
        is_confidential: true,
        allowed_grants: ['client_credentials'],
        redirect_uris_text: '',
        default_scopes: []
      });
      await fetchOauthClients();
    } catch (error) {
      console.error('Failed to create client', error);
      alert('Failed to create OAuth client');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleDeleteClient = async (oauthClientId) => {
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/clients/${oauthClientId}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }
      await fetchOauthClients();
    } catch (error) {
      console.error('Failed to delete OAuth client', error);
      alert('Failed to delete OAuth client');
    } finally {
      setAuthActionLoading(false);
    }
  };

  const runTokenDiagnostics = async () => {
    if (!diagnosticToken.trim()) return;
    try {
      setAuthActionLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/token/diagnostics`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: diagnosticToken.trim(),
          issuer: oauth2Config.issuer.trim(),
          audience: splitCsv(oauth2Config.audienceText)
        })
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }
      const data = await response.json();
      setDiagnosticResult(data);
    } catch (error) {
      console.error('Failed diagnostics', error);
      setDiagnosticResult({ valid: false, error: String(error) });
    } finally {
      setAuthActionLoading(false);
    }
  };

  const selectedOauthAudiences = splitCsv(oauth2Config.audienceText);
  const selectedOauthAlgorithms = splitCsv(oauth2Config.algorithmsText);
  const isSelfPreset = oauthIssuerPreset === 'self';

  const handleIssuerPresetChange = (presetId) => {
    const selectedPreset = OAUTH_ISSUER_PRESETS.find((item) => item.id === presetId) || OAUTH_ISSUER_PRESETS[0];
    setOauthIssuerPreset(selectedPreset.id);
    if (selectedPreset.id === 'custom') {
      return;
    }
    const issuer = selectedPreset.id === 'self' ? SELF_API_ISSUER : selectedPreset.issuer;
    const algorithms = Array.isArray(selectedPreset.defaultAlgorithms) && selectedPreset.defaultAlgorithms.length > 0
      ? selectedPreset.defaultAlgorithms
      : splitCsv(oauth2Config.algorithmsText);
    setOauth2Config((prev) => ({
      ...prev,
      issuer,
      jwks_url: selectedPreset.id === 'self' ? '' : (selectedPreset.jwksUrl || deriveJwksFromIssuer(issuer)),
      audienceText: selectedPreset.defaultAudience.length > 0 ? joinCsv(selectedPreset.defaultAudience) : prev.audienceText,
      algorithmsText: joinCsv(algorithms)
    }));
    if (selectedPreset.id === 'self') {
      setShowOauthSecret(false);
    }
  };

  const handleAutoPopulateJwks = () => {
    setOauth2Config((prev) => ({ ...prev, jwks_url: deriveJwksFromIssuer(prev.issuer) }));
  };

  const handleAddAudience = (value) => {
    const candidate = String(value || '').trim();
    if (!candidate) return;
    const current = splitCsv(oauth2Config.audienceText);
    if (current.includes(candidate)) return;
    setOauth2Config((prev) => ({ ...prev, audienceText: joinCsv([...current, candidate]) }));
    setOauthAudienceCandidate('');
  };

  const handleRemoveAudience = (value) => {
    const next = splitCsv(oauth2Config.audienceText).filter((item) => item !== value);
    setOauth2Config((prev) => ({ ...prev, audienceText: joinCsv(next) }));
  };

  const handleToggleAlgorithm = (algorithm) => {
    if (isSelfPreset) {
      return;
    }
    const current = splitCsv(oauth2Config.algorithmsText);
    const exists = current.includes(algorithm);
    const next = exists ? current.filter((item) => item !== algorithm) : [...current, algorithm];
    setOauth2Config((prev) => ({ ...prev, algorithmsText: joinCsv(next) }));
  };

  useEffect(() => {
    if (!isSelfPreset) return;
    if (oauth2Config.algorithmsText !== 'HS256' || oauth2Config.jwks_url) {
      setOauth2Config((prev) => ({
        ...prev,
        algorithmsText: 'HS256',
        jwks_url: ''
      }));
    }
    if (showOauthSecret) {
      setShowOauthSecret(false);
    }
  }, [isSelfPreset, oauth2Config.algorithmsText, oauth2Config.jwks_url, showOauthSecret]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const secret = String(oauth2Config.jwt_secret || '').trim();
      if (!secret) {
        if (!cancelled) {
          setTypedSecretFingerprint('');
        }
        return;
      }
      try {
        const hash = await sha256Hex(secret);
        if (!cancelled) {
          setTypedSecretFingerprint(hash ? hash.slice(0, 12) : '');
        }
      } catch (error) {
        if (!cancelled) {
          setTypedSecretFingerprint('');
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [oauth2Config.jwt_secret]);

  // Load account info and vector stores when account tab is active
  useEffect(() => {
    if (activeTab === 'account') {
      fetchAccountInfo();
      fetchVectorStores();
    }
  }, [activeTab, fetchAccountInfo, fetchVectorStores]);

  useEffect(() => {
    if (activeTab === 'auth') {
      loadAuthSettings();
    }
  }, [activeTab, loadAuthSettings]);

  useEffect(() => {
    if (activeTab === 'auth') {
      fetchAuthUsage(authUsageFilterKeyId).catch((error) => {
        console.error('Failed to refresh auth usage by filter:', error);
      });
    }
  }, [activeTab, authUsageFilterKeyId, fetchAuthUsage]);


  return (
    <div className="container-fluid mt-4">
      <div className="row mb-4">
        <div className="col">
          <h2>System Configuration</h2>
          <p className="text-muted">Configure AI providers, agents, and system settings</p>
        </div>
      </div>

      {/* Configuration Tabs */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'ai' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ai')}
                  >
                    <Bot size={20} className="me-2" />
                    AI Providers
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'agents' ? 'active' : ''}`}
                    onClick={() => setActiveTab('agents')}
                  >
                    <UserCheck size={20} className="me-2" />
                    AI Agents
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'pinecone' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pinecone')}
                  >
                    <Database size={20} className="me-2" />
                    Pinecone
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'ai_search' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ai_search')}
                  >
                    <Search size={20} className="me-2" />
                    AI Search
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'account' ? 'active' : ''}`}
                    onClick={() => setActiveTab('account')}
                  >
                    <User size={20} className="me-2" />
                    Account
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'auth' ? 'active' : ''}`}
                    onClick={() => setActiveTab('auth')}
                  >
                    <Shield size={20} className="me-2" />
                    Authentication
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                    onClick={() => setActiveTab('general')}
                  >
                    <Settings size={20} className="me-2" />
                    General
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'ai_openai' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ai_openai')}
                  >
                    <Bot size={20} className="me-2" />
                    AI (OpenAI)
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">


              {/* AI Configuration */}
              {activeTab === 'ai' && (
                <div>
                  {/* OpenAI Configuration */}
                  <div className="card border-0 bg-light mb-4">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <Bot size={20} className="text-primary" />
                        OpenAI Configuration
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">API Key</label>
                          <div className="input-group">
                            {!openaiKeyConfigured || openaiKeyEditing ? (
                              <input
                                type={showPassword ? 'text' : 'password'}
                                className="form-control"
                                value={openaiConfig.openai_api_key}
                                onChange={(e) => { openaiKeyTouched.current = true; setOpenaiConfig({...openaiConfig, openai_api_key: e.target.value}); }}
                                placeholder="Enter OpenAI API key"
                              />
                            ) : (
                              <input
                                type="password"
                                className="form-control"
                                value={openaiConfig.openai_api_key ? '••••••••••••••••' : 'configured'}
                                readOnly
                              />
                            )}
                            {(!openaiKeyConfigured || openaiKeyEditing) ? (
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                  setOpenaiKeyEditing(true);
                                  setOpenaiConfig({ ...openaiConfig, openai_api_key: '' });
                                }}
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label d-flex justify-content-between align-items-center">
                            Model (Token Limit)
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={fetchAvailableModels}
                              title="Refresh available models"
                              disabled={loadingModels}
                            >
                              {loadingModels ? (
                                <span className="spinner-border spinner-border-sm me-1" />
                              ) : (
                                <span>↻</span>
                              )}
                            </button>
                          </label>
                          <select
                            className="form-select"
                            value={openaiConfig.model}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, model: e.target.value})}
                          >
                            {availableModels.openai.length > 0 ? (
                              availableModels.openai.map((model) => (
                                <option key={model.id} value={model.id}>
                                  {formatModelDisplayName(model)}
                                </option>
                              ))
                            ) : (
                              <>
                                <option value="gpt-3.5-turbo">GPT-3.5 Turbo (16K)</option>
                                <option value="gpt-4">GPT-4 (128K)</option>
                                <option value="gpt-4-turbo">GPT-4 Turbo (128K)</option>
                                <option value="gpt-5">GPT-5 (8K)</option>
                                <option value="gpt-5-mini">GPT-5 Mini (16K)</option>
                              </>
                            )}
                          </select>
                          {availableModels.openai.length === 0 && (
                            <small className="text-muted">Loading models...</small>
                          )}
                          {availableModels.openai.length > 0 && (
                            <small className="text-muted">
                              {availableModels.openai.length} models available
                            </small>
                          )}
                          <small className="text-info d-block mt-1">
                            💡 Token limits indicate maximum context length. Higher limits allow processing longer documents.
                          </small>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Temperature</label>
                          <input
                            type="range"
                            className="form-range"
                            min="0"
                            max="2"
                            step="0.1"
                            value={openaiConfig.temperature}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, temperature: parseFloat(e.target.value)})}
                          />
                          <small className="text-muted">{openaiConfig.temperature}</small>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Max Tokens</label>
                          <input
                            type="number"
                            className="form-control"
                            value={openaiConfig.max_tokens}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, max_tokens: parseInt(e.target.value)})}
                            min="100"
                            max="4000"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary"
                          onClick={handleOpenAISave}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={20} className="me-2" />
                              Save OpenAI Config
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => testAIConnection('OpenAI')}
                        >
                          <TestTube size={20} className="me-2" />
                          Test OpenAI
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Anthropic Configuration */}
                  <div className="card border-0 bg-light">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <Bot size={20} className="text-success" />
                        Anthropic (Claude) Configuration
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">API Key</label>
                          <div className="input-group">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className="form-control"
                              value={anthropicConfig.api_key}
                              onChange={(e) => setAnthropicConfig({...anthropicConfig, api_key: e.target.value})}
                              placeholder="Enter Anthropic API key"
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label d-flex justify-content-between align-items-center">
                            Model (free-form)
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={fetchAvailableModels}
                              title="Fetch suggested models"
                              disabled={loadingModels}
                            >
                              {loadingModels ? (
                                <span className="spinner-border spinner-border-sm me-1" />
                              ) : (
                                <span>↻</span>
                              )}
                            </button>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={anthropicConfig.model}
                            onChange={(e) => setAnthropicConfig({...anthropicConfig, model: e.target.value})}
                            placeholder="e.g., claude-3-5-sonnet-20240620"
                            list="anthropic-model-suggestions"
                          />
                          <datalist id="anthropic-model-suggestions">
                            {availableModels.claude.length > 0 ? (
                              availableModels.claude.map((model) => (
                                <option key={model.id} value={model.id}>
                                  {formatModelDisplayName(model)}
                                </option>
                              ))
                            ) : (
                              <>
                                <option value="claude-3-5-sonnet-20240620">Claude 3.5 Sonnet</option>
                                <option value="claude-3-5-haiku-20240620">Claude 3.5 Haiku</option>
                                <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                                <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
                                <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
                              </>
                            )}
                          </datalist>
                          <small className="text-info d-block mt-1">
                            💡 Enter the exact Anthropic model ID. Suggestions are provided below; latest Sonnet: claude-3-5-sonnet-20240620.
                          </small>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Temperature</label>
                          <input
                            type="range"
                            className="form-range"
                            min="0"
                            max="2"
                            step="0.1"
                            value={anthropicConfig.temperature}
                            onChange={(e) => setAnthropicConfig({...anthropicConfig, temperature: parseFloat(e.target.value)})}
                          />
                          <small className="text-muted">{anthropicConfig.temperature}</small>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Max Tokens</label>
                          <input
                            type="number"
                            className="form-control"
                            value={anthropicConfig.max_tokens}
                            onChange={(e) => setAnthropicConfig({...anthropicConfig, max_tokens: parseInt(e.target.value)})}
                            min="100"
                            max="4000"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-success"
                          onClick={handleAnthropicSave}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={20} className="me-2" />
                              Save Anthropic Config
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => testAIConnection('Anthropic')}
                        >
                          <TestTube size={20} className="me-2" />
                          Test Anthropic
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Preferred AI Engine Configuration */}
                  <div className="card border-0 bg-light mt-4">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <Settings size={20} className="text-info" />
                        Preferred AI Engine
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Default AI Engine</label>
                          <select
                            className="form-select"
                            value={preferredAIEngine.preferred_engine}
                            onChange={(e) => setPreferredAIEngine({...preferredAIEngine, preferred_engine: e.target.value})}
                          >
                            <option value="auto">Auto - Let system choose best available</option>
                            <option value="openai">OpenAI GPT</option>
                            <option value="claude">Claude</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Description</label>
                          <input
                            type="text"
                            className="form-control"
                            value={preferredAIEngine.description}
                            onChange={(e) => setPreferredAIEngine({...preferredAIEngine, description: e.target.value})}
                            placeholder="Description of the preferred engine"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-info"
                          onClick={handlePreferredAISave}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={20} className="me-2" />
                              Save Preferred Engine
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Agents Configuration */}
              {activeTab === 'agents' && (
                <div className="row">
                  {/* Vertical Tabs Navigation */}
                  <div className="col-md-3">
                    <div className="nav flex-column nav-pills" role="tablist">
                      <button
                        className={`nav-link text-start ${activeAgentTab === 'sales-agent' ? 'active' : ''}`}
                        onClick={() => setActiveAgentTab('sales-agent')}
                        type="button"
                      >
                        <UserCheck size={18} className="me-2" />
                        AI Sales Agent
                      </button>
                      <button
                        className={`nav-link text-start ${activeAgentTab === 'tech-assistant' ? 'active' : ''}`}
                        onClick={() => setActiveAgentTab('tech-assistant')}
                        type="button"
                      >
                        <Wrench size={18} className="me-2" />
                        AI Tech Assistant
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="col-md-9">
                    <div className="tab-content">
                      {/* AI Sales Agent Tab */}
                      {activeAgentTab === 'sales-agent' && (
                        <div className="tab-pane fade show active">
                          <div className="card border-0 bg-light">
                            <div className="card-header bg-transparent">
                              <h5 className="mb-0 d-flex align-items-center gap-2">
                                <UserCheck size={24} className="text-primary" />
                                AI Sales Agent Configuration
                              </h5>
                            </div>
                            <div className="card-body">
                              <div className="row mb-3">
                                <div className="col-md-12">
                                  <div className="form-check form-switch mb-3">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="salesAgentEnabled"
                                      checked={aiSalesAgent.enabled}
                                      onChange={(e) => setAiSalesAgent({...aiSalesAgent, enabled: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="salesAgentEnabled">
                                      <strong>Enable AI Sales Agent</strong>
                                    </label>
                                  </div>
                                  <p className="text-muted small">
                                    An agent which explains about the product from the information on site and manufacturer site. 
                                    Leads with questions a user may want to ask, then passes over to tech assistant.
                                  </p>
                                </div>
                              </div>

                              {aiSalesAgent.enabled && (
                                <>
                                  <div className="row mb-3">
                                    <div className="col-md-6">
                                      <div className="form-check form-switch d-flex align-items-start">
                                        <input
                                          className="form-check-input me-2"
                                          type="checkbox"
                                          id="salesAgentCaching"
                                          checked={aiSalesAgent.caching_enabled}
                                          onChange={(e) => setAiSalesAgent({...aiSalesAgent, caching_enabled: e.target.checked})}
                                        />
                                        <div>
                                          <label className="form-check-label" htmlFor="salesAgentCaching">
                                            <strong>Enable Caching</strong>
                                          </label>
                                          <div>
                                            <small className="text-muted">
                                              Cache responses for faster performance and reduced costs.
                                            </small>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-6">
                                      <label className="form-label">Time to Chime In (seconds)</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        value={aiSalesAgent.chime_in_time}
                                        onChange={(e) => setAiSalesAgent({...aiSalesAgent, chime_in_time: parseInt(e.target.value)})}
                                        min="5"
                                        max="300"
                                      />
                                      <small className="text-muted">
                                        Time in seconds measuring how long the user is on a page before the agent chimes in with product information.
                                      </small>
                                    </div>
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-12">
                                      <label className="form-label">Product Scope</label>
                                      <div className="mb-2">
                                        <div className="form-check">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            name="productScope"
                                            id="allProducts"
                                            value="all"
                                            checked={aiSalesAgent.product_scope === 'all'}
                                            onChange={(e) => setAiSalesAgent({...aiSalesAgent, product_scope: e.target.value})}
                                          />
                                          <label className="form-check-label" htmlFor="allProducts">
                                            Show on all product pages
                                          </label>
                                        </div>
                                        <div className="form-check">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            name="productScope"
                                            id="selectedProducts"
                                            value="selected"
                                            checked={aiSalesAgent.product_scope === 'selected'}
                                            onChange={(e) => setAiSalesAgent({...aiSalesAgent, product_scope: e.target.value})}
                                          />
                                          <label className="form-check-label" htmlFor="selectedProducts">
                                            Show only on selected products
                                          </label>
                                        </div>
                                      </div>

                                      {aiSalesAgent.product_scope === 'selected' && (
                                        <div className="mt-3">
                                          <label className="form-label">Select Products</label>
                                          
                                          {/* Search Input */}
                                          <div className="mb-3">
                                            <input
                                              type="text"
                                              className="form-control"
                                              placeholder="Search products by code or name..."
                                              value={searchTerm}
                                              onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                          </div>

                                          <div className="row">
                                            {/* Selected Products */}
                                            <div className="col-md-6">
                                              <div className="card">
                                                <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                                                  <h6 className="mb-0">Selected Products ({selectedProductsPagination.total_count})</h6>
                                                  <button
                                                    className="btn btn-sm btn-outline-light"
                                                    onClick={clearAllProducts}
                                                    disabled={selectedProducts.length === 0}
                                                    title="Clear all selected products"
                                                  >
                                                    Clear All
                                                  </button>
                                                </div>
                                                <div className="card-body p-2" style={{ height: '250px', overflowY: 'auto' }}>
                                                  {loadingSelected ? (
                                                    <div className="text-center py-3">
                                                      <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                      </div>
                                                    </div>
                                                  ) : selectedProductsData.length === 0 ? (
                                                    <div className="text-muted text-center py-3">
                                                      <small>No products selected</small>
                                                    </div>
                                                  ) : (
                                                    <>
                                                      {selectedProductsData.map((product) => (
                                                        <div key={product.id} className="d-flex justify-content-between align-items-center p-2 border-bottom">
                                                          <div className="flex-grow-1">
                                                            <div className="fw-bold">{product.product_code}</div>
                                                            <div className="small text-muted">{product.name}</div>
                                                          </div>
                                                          <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => removeProduct(product.product_code)}
                                                            title="Remove from selection"
                                                          >
                                                            ×
                                                          </button>
                                                        </div>
                                                      ))}
                                                      
                                                      {/* Selected Products Pagination */}
                                                      {selectedProductsPagination.total_pages > 1 && (
                                                        <div className="d-flex justify-content-center mt-2">
                                                          <nav>
                                                            <ul className="pagination pagination-sm mb-0">
                                                              <li className={`page-item ${!selectedProductsPagination.has_prev ? 'disabled' : ''}`}>
                                                                <button
                                                                  className="page-link"
                                                                  onClick={() => loadSelectedProductsPage(selectedProductsPagination.current_page - 1)}
                                                                  disabled={!selectedProductsPagination.has_prev}
                                                                >
                                                                  Previous
                                                                </button>
                                                              </li>
                                                              <li className="page-item active">
                                                                <span className="page-link">
                                                                  {selectedProductsPagination.current_page} of {selectedProductsPagination.total_pages}
                                                                </span>
                                                              </li>
                                                              <li className={`page-item ${!selectedProductsPagination.has_next ? 'disabled' : ''}`}>
                                                                <button
                                                                  className="page-link"
                                                                  onClick={() => loadSelectedProductsPage(selectedProductsPagination.current_page + 1)}
                                                                  disabled={!selectedProductsPagination.has_next}
                                                                >
                                                                  Next
                                                                </button>
                                                              </li>
                                                            </ul>
                                                          </nav>
                                                        </div>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Available Products */}
                                            <div className="col-md-6">
                                              <div className="card">
                                                <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                                                  <h6 className="mb-0">Available Products ({availableProductsPagination.total_count})</h6>
                                                  <button
                                                    className="btn btn-sm btn-outline-light"
                                                    onClick={selectAllFromCurrentPage}
                                                    disabled={availableProductsData.length === 0}
                                                    title="Select all from current page"
                                                  >
                                                    Select Page
                                                  </button>
                                                </div>
                                                <div className="card-body p-2" style={{ height: '250px', overflowY: 'auto' }}>
                                                  {loadingAvailable ? (
                                                    <div className="text-center py-3">
                                                      <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                      </div>
                                                    </div>
                                                  ) : availableProductsData.length === 0 ? (
                                                    <div className="text-muted text-center py-3">
                                                      <small>No products available</small>
                                                    </div>
                                                  ) : (
                                                    <>
                                                      {availableProductsData.map((product) => (
                                                        <div key={product.id} className="d-flex justify-content-between align-items-center p-2 border-bottom">
                                                          <div className="flex-grow-1">
                                                            <div className="fw-bold">{product.product_code}</div>
                                                            <div className="small text-muted">{product.name}</div>
                                                          </div>
                                                          <button
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => addProduct(product.product_code)}
                                                            title="Add to selection"
                                                          >
                                                            +
                                                          </button>
                                                        </div>
                                                      ))}
                                                      
                                                      {/* Available Products Pagination */}
                                                      {availableProductsPagination.total_pages > 1 && (
                                                        <div className="d-flex justify-content-center mt-2">
                                                          <nav>
                                                            <ul className="pagination pagination-sm mb-0">
                                                              <li className={`page-item ${!availableProductsPagination.has_prev ? 'disabled' : ''}`}>
                                                                <button
                                                                  className="page-link"
                                                                  onClick={() => loadAvailableProductsPage(availableProductsPagination.current_page - 1)}
                                                                  disabled={!availableProductsPagination.has_prev}
                                                                >
                                                                  Previous
                                                                </button>
                                                              </li>
                                                              <li className="page-item active">
                                                                <span className="page-link">
                                                                  {availableProductsPagination.current_page} of {availableProductsPagination.total_pages}
                                                                </span>
                                                              </li>
                                                              <li className={`page-item ${!availableProductsPagination.has_next ? 'disabled' : ''}`}>
                                                                <button
                                                                  className="page-link"
                                                                  onClick={() => loadAvailableProductsPage(availableProductsPagination.current_page + 1)}
                                                                  disabled={!availableProductsPagination.has_next}
                                                                >
                                                                  Next
                                                                </button>
                                                              </li>
                                                            </ul>
                                                          </nav>
                                                        </div>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Selection Summary */}
                                          <div className="mt-3 text-center">
                                            <small className="text-muted">
                                              {selectedProducts.length} products selected
                                            </small>
                                          </div>

                                          <div className="mt-2">
                                            <small className="text-muted">
                                              Use the search box to filter products and click + to add or × to remove products.
                                            </small>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-12">
                                      <label className="form-label">System Prompt</label>
                                      <textarea
                                        className="form-control"
                                        rows="4"
                                        value={aiSalesAgent.system_prompt}
                                        onChange={(e) => setAiSalesAgent({...aiSalesAgent, system_prompt: e.target.value})}
                                        placeholder="Enter the system prompt that defines how the AI Sales Agent should behave..."
                                      />
                                      <small className="text-muted">
                                        This prompt defines the AI Sales Agent's personality, behavior, and instructions for interacting with customers.
                                      </small>
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* AI Sales Agent Testing Interface */}
                              {aiSalesAgent.enabled && (
                                <div className="mt-4">
                                  <div className="card border-primary">
                                    <div className="card-header bg-primary text-white">
                                      <h6 className="mb-0 d-flex align-items-center gap-2">
                                        <TestTube size={20} />
                                        Test AI Sales Agent
                                      </h6>
                                    </div>
                                    <div className="card-body">
                                      <div className="row mb-3">
                                        <div className="col-md-12">
                                          <label className="form-label fw-semibold">Product URL</label>
                                          <input
                                            type="url"
                                            className="form-control"
                                            value={testProductUrl}
                                            onChange={(e) => setTestProductUrl(e.target.value)}
                                            placeholder="Enter product URL (e.g., https://www.herculesindustries.com/...)"
                                          />
                                          <small className="text-muted">
                                            Enter the URL of the product page you want the AI Sales Agent to analyze.
                                          </small>
                                        </div>
                                      </div>
                                      
                                      <div className="row mb-3">
                                        <div className="col-md-12">
                                          <label className="form-label fw-semibold">Select Product</label>
                                          <select
                                            className="form-select"
                                            value={testSelectedProduct}
                                            onChange={(e) => setTestSelectedProduct(e.target.value)}
                                          >
                                            <option value="">Choose a product...</option>
                                            {aiSalesAgent.product_scope === 'all' ? (
                                              // If scope is 'all', show all available products
                                              availableProductsData?.map((product) => (
                                                <option key={product.product_code} value={product.product_code}>
                                                  {product.product_code} - {product.name}
                                                </option>
                                              ))
                                            ) : (
                                              // If scope is 'selected', show only selected products
                                              aiSalesAgent.selected_products?.map((productCode) => {
                                                const product = availableProductsData?.find(p => p.product_code === productCode);
                                                return (
                                                  <option key={productCode} value={productCode}>
                                                    {productCode} {product ? `- ${product.name}` : ''}
                                                  </option>
                                                );
                                              })
                                            )}
                                          </select>
                                          <small className="text-muted">
                                            {aiSalesAgent.product_scope === 'all' ? 
                                              'All products are available for testing.' :
                                              `Only products in the selected scope (${aiSalesAgent.selected_products?.length || 0} products) are available.`
                                            }
                                          </small>
                                        </div>
                                      </div>

                                      <div className="d-flex gap-2 mb-3">
                                        <button
                                          className="btn btn-success"
                                          onClick={handleTestSalesAgent}
                                          disabled={testingSalesAgent || !testProductUrl.trim() || !testSelectedProduct}
                                        >
                                          {testingSalesAgent ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm me-2" />
                                              Testing...
                                            </>
                                          ) : (
                                            <>
                                              <TestTube size={18} className="me-2" />
                                              Test AI Sales Agent
                                            </>
                                          )}
                                        </button>
                                        {showTestResults && (
                                          <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setShowTestResults(false)}
                                          >
                                            Hide Results
                                          </button>
                                        )}
                                      </div>

                                      {/* Test Results */}
                                      {showTestResults && (
                                        <div className="mt-3">
                                          <label className="form-label fw-semibold">AI Sales Agent Response</label>
                                          <div 
                                            className="form-control" 
                                            style={{ 
                                              minHeight: '200px', 
                                              maxHeight: '400px', 
                                              overflowY: 'auto',
                                              fontFamily: 'inherit',
                                              padding: '15px'
                                            }}
                                          >
                                            {testResults ? (
                                            testResults.startsWith('Error:') ? (
                                              <div className="text-danger">{testResults}</div>
                                            ) : (
                                              <div className="bg-light p-3 rounded border response-container">
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                  <Bot size={16} className="text-success" />
                                                  <span className="fw-semibold text-success">AI Sales Assistant Response</span>
                                                </div>
                                                <div className="clean-response">
                                                  <ReactMarkdown
                                                    components={{
                                                      code({ node, inline, className, children, ...props }) {
                                                        const match = /language-(\w+)/.exec(className || "");
                                                        return !inline && match ? (
                                                          <SyntaxHighlighter
                                                            style={materialLight}
                                                            language={match[1]}
                                                            PreTag="div"
                                                            {...props}
                                                          >
                                                            {String(children).replace(/\n$/, "")}
                                                          </SyntaxHighlighter>
                                                        ) : (
                                                          <code className={className} {...props}>
                                                            {children}
                                                          </code>
                                                        );
                                                      }
                                                    }}
                                                  >
                                                    {testResults}
                                                  </ReactMarkdown>
                                                </div>
                                              </div>
                                            )
                                          ) : (
                                            testingSalesAgent ? (
                                              <div className="text-muted">
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Generating AI response...
                                              </div>
                                            ) : (
                                              <div className="text-muted">Test results will appear here...</div>
                                            )
                                          )}
                                          </div>
                                          <small className="text-muted">
                                            This shows the real-time response from the AI Sales Agent endpoint.
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Sales Agent Save Button */}
                              <div className="d-flex gap-2 mt-4">
                                <button
                                  className="btn btn-primary"
                                  onClick={handleSalesAgentSave}
                                  disabled={saving}
                                >
                                  {saving ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" />
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <Save size={20} className="me-2" />
                                      Save Sales Agent Configuration
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* AI Tech Assistant Tab */}
                      {activeAgentTab === 'tech-assistant' && (
                        <div className="tab-pane fade show active">
                          <div className="card border-0 bg-light">
                            <div className="card-header bg-transparent">
                              <h5 className="mb-0 d-flex align-items-center gap-2">
                                <Wrench size={24} className="text-success" />
                                AI Tech Assistant Configuration
                              </h5>
                            </div>
                            <div className="card-body">
                              <div className="row mb-3">
                                <div className="col-md-12">
                                  <div className="form-check form-switch mb-3">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="techAssistantEnabled"
                                      checked={aiTechAssistant.enabled}
                                      onChange={(e) => setAiTechAssistant({...aiTechAssistant, enabled: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="techAssistantEnabled">
                                      <strong>Enable AI Tech Assistant</strong>
                                    </label>
                                  </div>
                                  <p className="text-muted small">
                                    Provides technical support and detailed guidance based on product manuals and specifications.
                                  </p>
                                </div>
                              </div>

                              {aiTechAssistant.enabled && (
                                <>
                                  <div className="row mb-3">
                                    <div className="col-md-12">
                                      <label className="form-label">Description</label>
                                      <textarea
                                        className="form-control"
                                        rows="3"
                                        value={aiTechAssistant.description}
                                        onChange={(e) => setAiTechAssistant({...aiTechAssistant, description: e.target.value})}
                                        placeholder="Describe what the AI Tech Assistant does..."
                                      />
                                      <small className="text-muted">
                                        This description helps users understand what the Tech Assistant can help them with.
                                      </small>
                                    </div>
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-12">
                                      <label className="form-label">System Prompt</label>
                                      <textarea
                                        className="form-control"
                                        rows="4"
                                        value={aiTechAssistant.system_prompt}
                                        onChange={(e) => setAiTechAssistant({...aiTechAssistant, system_prompt: e.target.value})}
                                        placeholder="Enter the system prompt that defines how the AI Tech Assistant should behave..."
                                      />
                                      <small className="text-muted">
                                        This prompt defines the AI Tech Assistant's expertise, tone, and instructions for providing technical support.
                                      </small>
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* Tech Assistant Save Button */}
                              <div className="d-flex gap-2 mt-4">
                                <button
                                  className="btn btn-primary"
                                  onClick={handleTechAssistantSave}
                                  disabled={saving}
                                >
                                  {saving ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" />
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <Save size={20} className="me-2" />
                                      Save Tech Assistant Configuration
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* General Configuration */}
              {activeTab === 'general' && (
                <div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">App Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={generalConfig.app_name}
                        onChange={(e) => setGeneralConfig({...generalConfig, app_name: e.target.value})}
                        placeholder="AI Tech Assistant"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Max File Size (MB)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={generalConfig.max_file_size}
                        onChange={(e) => setGeneralConfig({...generalConfig, max_file_size: parseInt(e.target.value)})}
                        min="1"
                        max="100"
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="form-label">Application Logo</label>
                      <div className="d-flex flex-wrap align-items-center gap-3">
                        {generalConfig?.branding?.logo_url ? (
                          <img
                            src={resolveLogoUrl(generalConfig.branding.logo_url)}
                            alt="Application Logo"
                            style={{ height: '60px', width: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}
                          />
                        ) : (
                          <div className="text-muted small">No logo uploaded yet.</div>
                        )}
                        <div>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (file) {
                                handleLogoUpload(file);
                                e.target.value = '';
                              }
                            }}
                            disabled={logoUploading}
                          />
                          <small className="text-muted d-block mt-1">
                            PNG, JPG, or SVG. Recommended height: 60px.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="form-label">Product Placeholder Image</label>
                      <div className="d-flex flex-wrap align-items-center gap-3">
                        {generalConfig?.branding?.product_placeholder_url ? (
                          <img
                            src={resolveLogoUrl(generalConfig.branding.product_placeholder_url)}
                            alt="Product Placeholder"
                            style={{ height: '60px', width: '60px', objectFit: 'contain', borderRadius: 8, border: '1px solid #e5e7eb' }}
                          />
                        ) : (
                          <div className="text-muted small">No placeholder uploaded yet.</div>
                        )}
                        <div>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (file) {
                                handleProductPlaceholderUpload(file);
                                e.target.value = '';
                              }
                            }}
                            disabled={placeholderUploading}
                          />
                          <small className="text-muted d-block mt-1">
                            PNG or JPG recommended. Used when product image is missing.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="form-label">Product Placeholder Image</label>
                      <div className="d-flex flex-wrap align-items-center gap-3">
                        {generalConfig?.branding?.product_placeholder_url ? (
                          <img
                            src={resolveLogoUrl(generalConfig.branding.product_placeholder_url)}
                            alt="Product Placeholder"
                            style={{ height: '60px', width: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}
                          />
                        ) : (
                          <div className="text-muted small">No placeholder image uploaded yet.</div>
                        )}
                        <div>
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (file) {
                                handleProductPlaceholderUpload(file);
                                e.target.value = '';
                              }
                            }}
                            disabled={placeholderUploading}
                          />
                          <small className="text-muted d-block mt-1">
                            PNG, JPG, or SVG. Recommended size: 80x80.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Chunk Size</label>
                      <input
                        type="number"
                        className="form-control"
                        value={generalConfig.chunk_size}
                        onChange={(e) => setGeneralConfig({...generalConfig, chunk_size: parseInt(e.target.value)})}
                        min="100"
                        max="5000"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Chunk Overlap</label>
                      <input
                        type="number"
                        className="form-control"
                        value={generalConfig.chunk_overlap}
                        onChange={(e) => setGeneralConfig({...generalConfig, chunk_overlap: parseInt(e.target.value)})}
                        min="0"
                        max="1000"
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Rate Limit (per minute)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={generalConfig.rate_limit_per_minute}
                        onChange={(e) => setGeneralConfig({...generalConfig, rate_limit_per_minute: parseInt(e.target.value)})}
                        min="10"
                        max="1000"
                      />
                    </div>
                  </div>
                  <div className="row mb-4">
                    <div className="col-12 col-xl-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <h6 className="mb-3">Left Menu Visibility</h6>
                          <div className="row">
                            {SIDEBAR_VISIBILITY_OPTIONS.map((item) => {
                              const hiddenItems = generalConfig?.ui_preferences?.hidden_sidebar_item_ids || [];
                              const isShown = !hiddenItems.includes(item.id);
                              return (
                                <div className="col-12 col-md-6 mb-2" key={item.id}>
                                  <div className="form-check form-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`sidebar-item-${item.id}`}
                                      checked={isShown}
                                      onChange={(e) =>
                                        toggleHiddenPreference('hidden_sidebar_item_ids', item.id, e.target.checked)
                                      }
                                    />
                                    <label className="form-check-label" htmlFor={`sidebar-item-${item.id}`}>
                                      {item.label}
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-xl-6 mt-3 mt-xl-0">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <h6 className="mb-3">Dashboard Card Visibility</h6>
                          <div className="row">
                            {DASHBOARD_CARD_VISIBILITY_OPTIONS.map((item) => {
                              const hiddenCards = generalConfig?.ui_preferences?.hidden_dashboard_card_ids || [];
                              const isShown = !hiddenCards.includes(item.id);
                              return (
                                <div className="col-12 col-md-6 mb-2" key={item.id}>
                                  <div className="form-check form-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`dashboard-card-${item.id}`}
                                      checked={isShown}
                                      onChange={(e) =>
                                        toggleHiddenPreference('hidden_dashboard_card_ids', item.id, e.target.checked)
                                      }
                                    />
                                    <label className="form-check-label" htmlFor={`dashboard-card-${item.id}`}>
                                      {item.label}
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={handleGeneralSave}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={20} className="me-2" />
                          Save Configuration
                        </>
                      )}
                    </button>
                  </div>

                </div>
              )}

              {/* Pinecone Configuration */}
              {activeTab === 'ai_search' && <AiSearchConfigPanel />}

              {activeTab === 'pinecone' && (
                <div>
                  <div className="card border-0 bg-light mb-4">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <Database size={20} className="text-primary" />
                        Pinecone Configuration
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">API Key</label>
                          <input
                            type="password"
                            className="form-control"
                            value={pineconeConfig.api_key}
                            onChange={(e) => setPineconeConfig({ ...pineconeConfig, api_key: e.target.value })}
                            placeholder="Enter Pinecone API key"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Index</label>
                          <input
                            className="form-control"
                            value={pineconeConfig.index}
                            onChange={(e) => setPineconeConfig({ ...pineconeConfig, index: e.target.value })}
                            placeholder="index-name"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Environment/Host</label>
                          <input
                            className="form-control"
                            value={pineconeConfig.environment}
                            onChange={(e) => setPineconeConfig({ ...pineconeConfig, environment: e.target.value })}
                            placeholder="e.g. us-east-1-aws or api-xxxxx.svc.us-east-1-aws.pinecone.io"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-3">
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            try {
                              setSaving(true);
                              const res = await fetch(`${API_BASE_URL}/api/v1/config/pinecone`, {
                                method: 'PUT',
                                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                                body: JSON.stringify(pineconeConfig)
                              });
                              if (!res.ok) {
                                const t = await res.text();
                                alert(`Save failed: ${res.status} ${t}`);
                              } else {
                                alert('Pinecone configuration saved');
                              }
                            } finally {
                              setSaving(false);
                            }
                          }}
                        >
                          <Save size={20} className="me-2" />
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Configuration */}
              {activeTab === 'account' && (
                <div>
                  <h5 className="mb-4">Account & Vector Store Management</h5>
                  
                  {/* Account Information */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card border-0 bg-light">
                        <div className="card-body">
                          <h6 className="card-title d-flex align-items-center">
                            <User size={20} className="me-2 text-primary" />
                            Account Information
                          </h6>
                          <div className="row">
                            <div className="col-md-6">
                              <label className="form-label">Account ID</label>
                              <input
                                type="text"
                                className="form-control"
                                value={accountInfo.account_id || 'Loading...'}
                                disabled
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Vector Store Pattern</label>
                              <input
                                type="text"
                                className="form-control"
                                value={`extremeb2b-${accountInfo.account_id || 'X'}`}
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Vector Stores Management */}
                  <div className="row">
                    <div className="col-12">
                      <div className="card">
                        <div className="card-header bg-transparent">
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 d-flex align-items-center">
                              <Database size={20} className="me-2 text-success" />
                              Vector Stores
                            </h6>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => setShowAddVectorStore(true)}
                              disabled={vectorStores.length >= (accountInfo.max_vector_store || 1)}
                              title={vectorStores.length >= (accountInfo.max_vector_store || 1) 
                                ? `Maximum vector store limit reached (${accountInfo.max_vector_store || 1})` 
                                : 'Add a new vector store'}
                            >
                              <Plus size={16} className="me-1" />
                              Add Vector Store
                              {vectorStores.length >= (accountInfo.max_vector_store || 1) && (
                                <span className="badge bg-warning ms-1">Limit Reached</span>
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          {vectorStores.length === 0 ? (
                            <div className="text-center text-muted py-4">
                              <Database size={48} className="mb-2 opacity-50" />
                              <p>No vector stores configured yet.</p>
                              <p className="small">Create a vector store to organize your document embeddings.</p>
                            </div>
                          ) : (
                            <div className="row">
                                                                      {vectorStores.map((store, index) => (
                                            <div key={index} className="col-md-6 mb-3">
                                                <div className={`card border ${defaultVectorStoreIndex === index ? 'border-success' : ''}`}>
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div className="d-flex align-items-center">
                                                                <h6 className="card-title mb-0 me-2">{store.name}</h6>
                                                                {defaultVectorStoreIndex === index && (
                                                                    <span className="badge bg-success">Default</span>
                                                                )}
                                                                {store.assistant_id && (
                                                                    <span className="badge bg-info ms-1" title={store.assistant_name}>
                                                                        🤖 Assistant
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="dropdown">
                                                                <button
                                                                    className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                                                    type="button"
                                                                    data-bs-toggle="dropdown"
                                                                >
                                                                    Actions
                                                                </button>
                                                                <ul className="dropdown-menu">
                                                                    {defaultVectorStoreIndex !== index && (
                                                                        <li>
                                                                            <button
                                                                                className="dropdown-item"
                                                                                onClick={() => handleSetDefaultVectorStore(index)}
                                                                            >
                                                                                <Database size={14} className="me-2" />
                                                                                Set as Default
                                                                            </button>
                                                                        </li>
                                                                    )}
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handleEditVectorStore(store, index)}
                                                                        >
                                                                            <Edit2 size={14} className="me-2" />
                                                                            Edit
                                                                        </button>
                                                                    </li>
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item text-danger"
                                                                            onClick={() => handleDeleteVectorStore(index)}
                                                                        >
                                                                            <Trash2 size={14} className="me-2" />
                                                                            Delete
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                        <p className="card-text text-muted small mb-2">{store.description}</p>
                                                        <div className="d-flex justify-content-between text-small">
                                                            <span className="badge bg-primary">{store.vector_store_id}</span>
                                                            <span className="text-muted">
                                                                Created: {new Date(store.created_at).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'auth' && (
                <div className="container-fluid px-0">
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <h5 className="mb-0">Account Authentication</h5>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={loadAuthSettings}
                      disabled={authLoading || authActionLoading}
                    >
                      <RefreshCw size={16} className="me-2" />
                      Refresh
                    </button>
                  </div>

                  {authLoading && (
                    <div className="alert alert-info py-2">Loading authentication settings...</div>
                  )}

                  {newAuthKeyPlaintext && (
                    <div className="alert alert-warning">
                      <div className="fw-semibold mb-1">Copy this API key now. It will not be shown again.</div>
                      <code className="d-block p-2 bg-white border rounded">{newAuthKeyPlaintext}</code>
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-12 col-xl-6">
                      <div className="card border-0 bg-light h-100">
                        <div className="card-header bg-transparent">
                          <h6 className="mb-0 d-flex align-items-center">
                            <Key size={18} className="me-2 text-primary" />
                            API Keys
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="row g-2 mb-3">
                            <div className="col-12 col-lg-5">
                              <label className="form-label">Key Name</label>
                              <input
                                className="form-control"
                                value={newAuthKeyName}
                                onChange={(e) => setNewAuthKeyName(e.target.value)}
                                placeholder="e.g. Webhook integration"
                              />
                            </div>
                            <div className="col-12 col-lg-5">
                              <label className="form-label">Description</label>
                              <input
                                className="form-control"
                                value={newAuthKeyDescription}
                                onChange={(e) => setNewAuthKeyDescription(e.target.value)}
                                placeholder="Optional details"
                              />
                            </div>
                            <div className="col-12 col-lg-2 d-grid align-self-end">
                              <button
                                className="btn btn-primary"
                                onClick={handleCreateAuthKey}
                                disabled={authActionLoading}
                              >
                                Create
                              </button>
                            </div>
                          </div>

                          <div className="table-responsive">
                            <table className="table table-sm align-middle">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Prefix</th>
                                  <th>Status</th>
                                  <th>Usage</th>
                                  <th className="text-end">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {authKeys.length === 0 ? (
                                  <tr>
                                    <td colSpan={5} className="text-muted text-center py-3">No account keys yet</td>
                                  </tr>
                                ) : (
                                  authKeys.map((keyItem) => (
                                    <tr key={keyItem.id}>
                                      <td>
                                        <div className="fw-semibold">{keyItem.name}</div>
                                        <div className="small text-muted">{keyItem.description || 'No description'}</div>
                                      </td>
                                      <td className="text-monospace">
                                        {keyItem.key_prefix}...{keyItem.key_last4}
                                      </td>
                                      <td>
                                        <span className={`badge ${keyItem.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                          {keyItem.is_active ? 'Active' : 'Disabled'}
                                        </span>
                                      </td>
                                      <td>
                                        <div>{keyItem.usage_count || 0}</div>
                                        <div className="small text-muted">
                                          {keyItem.last_used_at ? new Date(keyItem.last_used_at).toLocaleString() : 'Never'}
                                        </div>
                                      </td>
                                      <td className="text-end">
                                        <div className="btn-group btn-group-sm">
                                          <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => openAuthKeyEditModal(keyItem)}
                                            disabled={authActionLoading}
                                          >
                                            Edit
                                          </button>
                                          <button
                                            className="btn btn-outline-primary"
                                            onClick={() => handleRotateAuthKey(keyItem.id)}
                                            disabled={authActionLoading}
                                          >
                                            Rotate
                                          </button>
                                          {keyItem.is_active && (
                                            <button
                                              className="btn btn-outline-danger"
                                              onClick={() => handleDeactivateAuthKey(keyItem.id)}
                                              disabled={authActionLoading}
                                            >
                                              Disable
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-xl-6">
                      <div className="card border-0 bg-light h-100">
                        <div className="card-header bg-transparent">
                          <h6 className="mb-0 d-flex align-items-center">
                            <Shield size={18} className="me-2 text-success" />
                            OAuth2 / JWT Validation
                          </h6>
                        </div>
                        <div className="card-body">
                          <div className="form-check form-switch mb-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="oauth2Enabled"
                              checked={oauth2Config.enabled}
                              onChange={(e) => setOauth2Config((prev) => ({ ...prev, enabled: e.target.checked }))}
                            />
                            <label className="form-check-label" htmlFor="oauth2Enabled">
                              Enable OAuth2 bearer token authentication
                            </label>
                          </div>
                          <div className="row g-2">
                            <div className="col-12">
                              <label className="form-label">Identity Provider Preset</label>
                              <select
                                className="form-select"
                                value={oauthIssuerPreset}
                                onChange={(e) => handleIssuerPresetChange(e.target.value)}
                              >
                                {OAUTH_ISSUER_PRESETS.map((preset) => (
                                  <option key={preset.id} value={preset.id}>{preset.label}</option>
                                ))}
                              </select>
                              <small className="text-muted">
                                Choose a provider to auto-fill issuer, audience and JWKS URL, or keep Custom.
                              </small>
                              {oauthIssuerPreset === 'self' && (
                                <div className="alert alert-info mt-2 mb-0 py-2">
                                  <small>
                                    Self preset uses this API issuer: <code>{oauth2SelfHint.issuer || SELF_API_ISSUER || '(not resolved)'}</code>.
                                    For self-issued HS256 tokens, JWKS is disabled and algorithm is locked to HS256.
                                    Use your account-specific self signing secret ({oauth2SelfHint.secret_source === 'account_oauth2_jwt_secret' ? 'configured' : 'fallback currently active'}).
                                  </small>
                                  <div className="mt-2 d-flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={handleGenerateSelfSigningSecret}
                                      disabled={authActionLoading}
                                    >
                                      Generate Secure Self Signing Secret
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={handleRotateSelfSigningSecret}
                                      disabled={authActionLoading}
                                    >
                                      Rotate Secret and Revoke Active Tokens
                                    </button>
                                  </div>
                                  {selfSecretGeneratedValue && (
                                    <small className="d-block mt-2">
                                      Generated secret (copy now): <code>{selfSecretGeneratedValue}</code>
                                    </small>
                                  )}
                                  <div className="mt-2">
                                    <small className="d-block">
                                      Active secret fingerprint: <code>{oauth2SelfHint.secret_fingerprint || '(not available)'}</code>
                                    </small>
                                    <small className="d-block">
                                      Typed value fingerprint: <code>{typedSecretFingerprint || '(start typing to verify)'}</code>
                                    </small>
                                    <small className="d-block fw-semibold">
                                      Match status: {typedSecretFingerprint && oauth2SelfHint.secret_fingerprint
                                        ? (typedSecretFingerprint === oauth2SelfHint.secret_fingerprint ? 'Matched' : 'Not matched')
                                        : 'Pending'}
                                    </small>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="col-12">
                              <label className="form-label">Issuer (iss)</label>
                              <input
                                className="form-control"
                                value={oauth2Config.issuer}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setOauth2Config((prev) => ({ ...prev, issuer: value }));
                                  setOauthIssuerPreset(inferIssuerPreset(value));
                                }}
                                placeholder="https://issuer.example.com"
                              />
                            </div>
                            <div className="col-12">
                              <label className="form-label">Audience</label>
                              <div className="row g-2">
                                <div className="col-12 col-md-7">
                                  <select
                                    className="form-select"
                                    value={oauthAudienceCandidate}
                                    onChange={(e) => setOauthAudienceCandidate(e.target.value)}
                                  >
                                    <option value="">Select common audience</option>
                                    {OAUTH_AUDIENCE_OPTIONS.map((audience) => (
                                      <option key={audience} value={audience}>{audience}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="col-12 col-md-5 d-grid">
                                  <button
                                    className="btn btn-outline-primary"
                                    type="button"
                                    onClick={() => handleAddAudience(oauthAudienceCandidate)}
                                  >
                                    Add selected audience
                                  </button>
                                </div>
                                <div className="col-12">
                                  <div className="input-group">
                                    <input
                                      className="form-control"
                                      placeholder="Or type a custom audience and click Add"
                                      value={oauthAudienceCandidate}
                                      onChange={(e) => setOauthAudienceCandidate(e.target.value)}
                                    />
                                    <button
                                      className="btn btn-outline-secondary"
                                      type="button"
                                      onClick={() => handleAddAudience(oauthAudienceCandidate)}
                                    >
                                      Add
                                    </button>
                                  </div>
                                </div>
                                <div className="col-12">
                                  {selectedOauthAudiences.length === 0 ? (
                                    <small className="text-muted">No audiences selected yet.</small>
                                  ) : (
                                    <div className="d-flex flex-wrap gap-2">
                                      {selectedOauthAudiences.map((aud) => (
                                        <span key={aud} className="badge bg-primary d-inline-flex align-items-center">
                                          {aud}
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-link text-white text-decoration-none p-0 ms-2"
                                            onClick={() => handleRemoveAudience(aud)}
                                            aria-label={`Remove ${aud}`}
                                          >
                                            x
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-12">
                              <label className="form-label d-flex justify-content-between align-items-center">
                                <span>JWKS URL</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={handleAutoPopulateJwks}
                                  disabled={isSelfPreset}
                                >
                                  Auto-populate from issuer
                                </button>
                              </label>
                              <input
                                className="form-control"
                                value={oauth2Config.jwks_url}
                                onChange={(e) => setOauth2Config((prev) => ({ ...prev, jwks_url: e.target.value }))}
                                placeholder="https://issuer.example.com/.well-known/jwks.json"
                                disabled={isSelfPreset}
                              />
                              {isSelfPreset && (
                                <small className="text-muted">JWKS is not required for self-issued HS256 tokens.</small>
                              )}
                            </div>
                            <div className="col-12">
                              <label className="form-label">Allowed Algorithms</label>
                              <div className="row row-cols-2 row-cols-md-3 g-2">
                                {OAUTH_ALGORITHM_OPTIONS.map((algorithm) => (
                                  <div className="col" key={algorithm}>
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`alg-${algorithm}`}
                                        checked={selectedOauthAlgorithms.includes(algorithm)}
                                        onChange={() => handleToggleAlgorithm(algorithm)}
                                        disabled={isSelfPreset && algorithm !== 'HS256'}
                                      />
                                      <label className="form-check-label" htmlFor={`alg-${algorithm}`}>
                                        {algorithm}
                                      </label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="col-12">
                              <label className="form-label">JWT Secret (optional when JWKS is used)</label>
                              <input
                                type={isSelfPreset ? 'password' : (showOauthSecret ? 'text' : 'password')}
                                className="form-control"
                                value={oauth2Config.jwt_secret}
                                onChange={(e) => setOauth2Config((prev) => ({ ...prev, jwt_secret: e.target.value }))}
                                placeholder={isSelfPreset ? 'Enter backend SECRET_KEY value' : (oauth2Config.has_jwt_secret ? 'Stored secret exists. Enter only to replace.' : 'Enter shared secret')}
                              />
                              {isSelfPreset ? (
                                <small className="text-muted d-block mt-2">
                                  Use the generated account self signing secret; do not use the global backend secret.
                                </small>
                              ) : (
                                <div className="form-check mt-2">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="showOauthSecret"
                                    checked={showOauthSecret}
                                    onChange={(e) => setShowOauthSecret(e.target.checked)}
                                  />
                                  <label className="form-check-label" htmlFor="showOauthSecret">
                                    Show JWT secret
                                  </label>
                                </div>
                              )}
                            </div>
                            {oauth2Config.has_jwt_secret && (
                              <div className="col-12">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clearSecret"
                                    checked={oauth2Config.clear_jwt_secret}
                                    onChange={(e) => setOauth2Config((prev) => ({ ...prev, clear_jwt_secret: e.target.checked }))}
                                  />
                                  <label className="form-check-label" htmlFor="clearSecret">
                                    Clear existing JWT secret
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="d-grid d-sm-flex gap-2 mt-3">
                            <button
                              className="btn btn-success"
                              onClick={handleSaveOAuth2Config}
                              disabled={authActionLoading}
                            >
                              Save OAuth2 Settings
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="card border-0 bg-light">
                        <div className="card-header bg-transparent">
                          <h6 className="mb-0">External OIDC Providers</h6>
                        </div>
                        <div className="card-body">
                          <div className="row g-2 mb-3">
                            <div className="col-12 col-lg-3">
                              <input
                                className="form-control"
                                placeholder="Provider name"
                                value={providerForm.provider_name}
                                onChange={(e) => setProviderForm((prev) => ({ ...prev, provider_name: e.target.value }))}
                              />
                            </div>
                            <div className="col-12 col-lg-4">
                              <input
                                className="form-control"
                                placeholder="Issuer URL"
                                value={providerForm.issuer}
                                onChange={(e) => setProviderForm((prev) => ({ ...prev, issuer: e.target.value }))}
                              />
                            </div>
                            <div className="col-12 col-lg-3">
                              <input
                                className="form-control"
                                placeholder="JWKS URL"
                                value={providerForm.jwks_url}
                                onChange={(e) => setProviderForm((prev) => ({ ...prev, jwks_url: e.target.value }))}
                              />
                            </div>
                            <div className="col-12 col-lg-2 d-grid">
                              <button className="btn btn-primary" onClick={handleCreateProvider} disabled={authActionLoading}>
                                Add Provider
                              </button>
                            </div>
                            <div className="col-12 col-lg-6">
                              <input
                                className="form-control"
                                placeholder="Audience (comma-separated)"
                                value={providerForm.audienceText}
                                onChange={(e) => setProviderForm((prev) => ({ ...prev, audienceText: e.target.value }))}
                              />
                            </div>
                            <div className="col-12 col-lg-4">
                              <input
                                className="form-control"
                                placeholder="Algorithms (comma-separated)"
                                value={providerForm.allowedAlgorithmsText}
                                onChange={(e) => setProviderForm((prev) => ({ ...prev, allowedAlgorithmsText: e.target.value }))}
                              />
                            </div>
                            <div className="col-12 col-lg-2 d-flex align-items-center">
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="provider-active"
                                  checked={providerForm.is_active}
                                  onChange={(e) => setProviderForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="provider-active">Active</label>
                              </div>
                            </div>
                          </div>
                          <div className="table-responsive">
                            <table className="table table-sm align-middle">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Issuer</th>
                                  <th>Algorithms</th>
                                  <th>Status</th>
                                  <th className="text-end">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {oauthProviders.length === 0 ? (
                                  <tr>
                                    <td colSpan={5} className="text-center text-muted py-2">No providers configured</td>
                                  </tr>
                                ) : (
                                  oauthProviders.map((provider) => (
                                    <tr key={provider.id}>
                                      <td>{provider.provider_name}</td>
                                      <td className="text-break">{provider.issuer}</td>
                                      <td>{Array.isArray(provider.allowed_algorithms) ? provider.allowed_algorithms.join(', ') : ''}</td>
                                      <td>
                                        <span className={`badge ${provider.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                          {provider.is_active ? 'Active' : 'Disabled'}
                                        </span>
                                      </td>
                                      <td className="text-end">
                                        <button
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => handleDeleteProvider(provider.id)}
                                          disabled={authActionLoading}
                                        >
                                          Delete
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="card border-0 bg-light">
                        <div className="card-header bg-transparent">
                          <h6 className="mb-0">OAuth Scopes and Clients</h6>
                        </div>
                        <div className="card-body">
                          <div className="row g-3 mb-3">
                            <div className="col-12 col-xl-4">
                              <div className="border rounded p-3 bg-white h-100">
                                <h6 className="mb-2">Scopes</h6>
                                <div className="input-group mb-2">
                                  <input
                                    className="form-control"
                                    placeholder="scope name (e.g. api.read)"
                                    value={newScopeName}
                                    onChange={(e) => setNewScopeName(e.target.value)}
                                  />
                                  <button className="btn btn-outline-primary" onClick={handleCreateScope} disabled={authActionLoading}>
                                    Add
                                  </button>
                                </div>
                                <input
                                  className="form-control mb-2"
                                  placeholder="scope description"
                                  value={newScopeDescription}
                                  onChange={(e) => setNewScopeDescription(e.target.value)}
                                />
                                <div className="d-flex flex-wrap gap-2">
                                  {oauthScopes.length === 0 ? (
                                    <small className="text-muted">No scopes yet</small>
                                  ) : (
                                    oauthScopes.map((scope) => (
                                      <span key={scope.id} className="badge bg-primary">{scope.scope_name}</span>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-xl-8">
                              <div className="border rounded p-3 bg-white h-100">
                                <h6 className="mb-2">Create OAuth Client</h6>
                                {createdClientSecret && (
                                  <div className="alert alert-warning py-2">
                                    New client secret (copy now): <code>{createdClientSecret}</code>
                                  </div>
                                )}
                                <div className="row g-2">
                                  <div className="col-12 col-md-4">
                                    <input
                                      className="form-control"
                                      placeholder="Client name"
                                      value={clientForm.client_name}
                                      onChange={(e) => setClientForm((prev) => ({ ...prev, client_name: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12 col-md-4">
                                    <input
                                      className="form-control"
                                      placeholder="Client ID (optional)"
                                      value={clientForm.client_id}
                                      onChange={(e) => setClientForm((prev) => ({ ...prev, client_id: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12 col-md-4">
                                    <input
                                      className="form-control"
                                      placeholder="Client secret (optional, generated if blank)"
                                      value={clientForm.client_secret}
                                      onChange={(e) => setClientForm((prev) => ({ ...prev, client_secret: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12 col-md-6">
                                    <input
                                      className="form-control"
                                      placeholder="Redirect URIs (comma-separated)"
                                      value={clientForm.redirect_uris_text}
                                      onChange={(e) => setClientForm((prev) => ({ ...prev, redirect_uris_text: e.target.value }))}
                                    />
                                  </div>
                                  <div className="col-12 col-md-3 d-flex align-items-center">
                                    <div className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="client-confidential"
                                        checked={clientForm.is_confidential}
                                        onChange={(e) => setClientForm((prev) => ({ ...prev, is_confidential: e.target.checked }))}
                                      />
                                      <label className="form-check-label" htmlFor="client-confidential">Confidential</label>
                                    </div>
                                  </div>
                                  <div className="col-12 col-md-3 d-grid">
                                    <button className="btn btn-success" onClick={handleCreateClient} disabled={authActionLoading}>
                                      Create Client
                                    </button>
                                  </div>
                                  <div className="col-12 col-md-6">
                                    <div className="d-flex flex-wrap gap-2">
                                      {['client_credentials', 'authorization_code'].map((grant) => (
                                        <div className="form-check" key={grant}>
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`grant-${grant}`}
                                            checked={clientForm.allowed_grants.includes(grant)}
                                            onChange={() => toggleClientGrant(grant)}
                                          />
                                          <label className="form-check-label" htmlFor={`grant-${grant}`}>{grant}</label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="col-12 col-md-6">
                                    <div className="d-flex flex-wrap gap-2">
                                      {oauthScopes.map((scope) => (
                                        <div className="form-check" key={scope.id}>
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`scope-${scope.id}`}
                                            checked={clientForm.default_scopes.includes(scope.scope_name)}
                                            onChange={() => toggleClientScope(scope.scope_name)}
                                          />
                                          <label className="form-check-label" htmlFor={`scope-${scope.id}`}>{scope.scope_name}</label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="table-responsive">
                            <table className="table table-sm align-middle">
                              <thead>
                                <tr>
                                  <th>Client</th>
                                  <th>Grants</th>
                                  <th>Scopes</th>
                                  <th>Status</th>
                                  <th className="text-end">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {oauthClients.length === 0 ? (
                                  <tr>
                                    <td colSpan={5} className="text-center text-muted py-2">No OAuth clients created</td>
                                  </tr>
                                ) : (
                                  oauthClients.map((client) => (
                                    <tr key={client.id}>
                                      <td>
                                        <div className="fw-semibold">{client.client_name}</div>
                                        <div className="small text-muted">{client.client_id}</div>
                                      </td>
                                      <td>{Array.isArray(client.allowed_grants) ? client.allowed_grants.join(', ') : ''}</td>
                                      <td>{Array.isArray(client.default_scopes) ? client.default_scopes.join(', ') : ''}</td>
                                      <td>
                                        <span className={`badge ${client.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                          {client.is_active ? 'Active' : 'Disabled'}
                                        </span>
                                      </td>
                                      <td className="text-end">
                                        <button
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => handleDeleteClient(client.id)}
                                          disabled={authActionLoading}
                                        >
                                          Delete
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="card border-0 bg-light">
                        <div className="card-header bg-transparent">
                          <h6 className="mb-0">Token Diagnostics</h6>
                        </div>
                        <div className="card-body">
                          <div className="row g-2">
                            <div className="col-12 col-lg-10">
                              <textarea
                                className="form-control"
                                rows="3"
                                placeholder="Paste bearer token to validate claims and signature"
                                value={diagnosticToken}
                                onChange={(e) => setDiagnosticToken(e.target.value)}
                              />
                            </div>
                            <div className="col-12 col-lg-2 d-grid">
                              <button className="btn btn-outline-primary" onClick={runTokenDiagnostics} disabled={authActionLoading}>
                                Validate Token
                              </button>
                            </div>
                            <div className="col-12">
                              {diagnosticResult ? (
                                <pre className="small bg-white border rounded p-2 mb-0" style={{ maxHeight: '260px', overflow: 'auto' }}>
                                  {JSON.stringify(diagnosticResult, null, 2)}
                                </pre>
                              ) : (
                                <small className="text-muted">Run diagnostics to verify token claims, scope and signature result.</small>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="card border-0 bg-light">
                        <div className="card-header bg-transparent">
                          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <h6 className="mb-0">Authentication Usage Log</h6>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <select
                                className="form-select form-select-sm"
                                style={{ minWidth: '220px' }}
                                value={authUsageFilterKeyId}
                                onChange={(e) => setAuthUsageFilterKeyId(e.target.value)}
                              >
                                <option value="">All keys and auth methods</option>
                                {authKeys.map((keyItem) => (
                                  <option key={keyItem.id} value={keyItem.id}>
                                    {keyItem.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => fetchAuthUsage(authUsageFilterKeyId)}
                                disabled={authActionLoading}
                              >
                                Reload Logs
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-sm align-middle">
                              <thead>
                                <tr>
                                  <th>Timestamp</th>
                                  <th>Method</th>
                                  <th>Auth Type</th>
                                  <th>Key</th>
                                  <th>IP</th>
                                  <th>Path</th>
                                </tr>
                              </thead>
                              <tbody>
                                {authUsageEvents.length === 0 ? (
                                  <tr>
                                    <td colSpan={6} className="text-muted text-center py-3">No usage events found</td>
                                  </tr>
                                ) : (
                                  authUsageEvents.map((event, idx) => (
                                    <tr key={`${event.ts || 'na'}-${idx}`}>
                                      <td>{event.ts ? new Date(event.ts).toLocaleString() : '-'}</td>
                                      <td>{event.method || '-'}</td>
                                      <td>{event.auth_type || '-'}</td>
                                      <td>{event.key_name || (event.key_id ? `key:${event.key_id}` : '-')}</td>
                                      <td>{event.ip || '-'}</td>
                                      <td className="text-break">{event.path || '-'}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                          <small className="text-muted">
                            Events are sourced from rotating backend text logs for Logstash ingestion.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI (OpenAI) Configuration */}
              {activeTab === 'ai_openai' && (
                <div>
                  {/* OpenAI Configuration */}
                  <div className="card border-0 bg-light mb-4">
                    <div className="card-body">
                      <h5 className="card-title">OpenAI Configuration</h5>
                      {/* reuse existing OpenAI inputs block */}
                      {/* BEGIN OPENAI BLOCK */}
                      <div className="row g-3">
                        <div className="col-md-12">
                          <label className="form-label">API Key</label>
                          <div className="input-group">
                            {!openaiKeyConfigured || openaiKeyEditing ? (
                              <input
                                type={showPassword ? 'text' : 'password'}
                                className="form-control"
                                value={openaiConfig.openai_api_key}
                                onChange={(e) => { openaiKeyTouched.current = true; setOpenaiConfig({...openaiConfig, openai_api_key: e.target.value}); }}
                                placeholder="Enter OpenAI API key"
                              />
                            ) : (
                              <input
                                type="password"
                                className="form-control"
                                value={openaiConfig.openai_api_key ? '••••••••••••••••' : 'configured'}
                                readOnly
                              />
                            )}
                            {(!openaiKeyConfigured || openaiKeyEditing) ? (
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                  setOpenaiKeyEditing(true);
                                  setOpenaiConfig({ ...openaiConfig, openai_api_key: '' });
                                }}
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Model</label>
                          <select
                            className="form-select"
                            value={openaiConfig.model}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, model: e.target.value})}
                          >
                            {availableModels.openai.length > 0 ? (
                              availableModels.openai.map((model, idx) => (
                                <option key={idx} value={model}>{model}</option>
                              ))
                            ) : (
                              <>
                                <option value="gpt-4o">gpt-4o</option>
                                <option value="gpt-4o-mini">gpt-4o-mini</option>
                                <option value="gpt-4-turbo">gpt-4-turbo</option>
                              </>
                            )}
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Temperature</label>
                          <input
                            type="range"
                            className="form-range"
                            min="0"
                            max="2"
                            step="0.1"
                            value={openaiConfig.temperature}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, temperature: parseFloat(e.target.value)})}
                          />
                          <small className="text-muted">{openaiConfig.temperature}</small>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Max Tokens</label>
                          <input
                            type="number"
                            className="form-control"
                            value={openaiConfig.max_tokens}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, max_tokens: parseInt(e.target.value)})}
                            min="100"
                            max="4000"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-3">
                        <button className="btn btn-primary" onClick={handleOpenAISave} disabled={saving}>
                          {saving ? (<><span className="spinner-border spinner-border-sm me-2" />Saving...</>) : 'Save OpenAI'}
                        </button>
                        <button className="btn btn-outline-secondary" onClick={testAIConnection}>
                          Test Connection
                        </button>
                      </div>
                      {/* END OPENAI BLOCK */}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Add/Edit Vector Store Modal - Right Sliding */}
      {(showAddVectorStore || editingVectorStore) && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div 
            className="modal-dialog modal-dialog-slideright" 
            style={{ 
              position: 'fixed',
              right: 0,
              top: 0,
              height: '100vh',
              width: '50%',
              maxWidth: '75%',
              minWidth: '40%',
              margin: 0,
              resize: 'horizontal',
              overflow: 'auto'
            }}
          >
            <div className="modal-content" style={{ height: '100%', borderRadius: '0' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1000 }}>
                <h5 className="modal-title">
                  🤖 {editingVectorStore ? 'Edit Vector Store & Assistant' : 'Add New Vector Store & Assistant'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseVectorStoreModal}
                ></button>
              </div>
              <div className="modal-body" style={{ padding: '1.5rem', overflow: 'auto', flex: 1 }}>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={vectorStoreForm.name}
                    onChange={(e) => setVectorStoreForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Product Documentation Store"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={vectorStoreForm.description}
                    onChange={(e) => setVectorStoreForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this vector store will contain..."
                  />
                </div>
                
                {/* Assistant Configuration Section */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">🤖 Assistant Configuration</h6>
                    <small className="text-muted">Configure the AI assistant that will be attached to this vector store</small>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">Assistant Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={vectorStoreForm.assistant.name}
                        onChange={(e) => setVectorStoreForm(prev => ({ 
                          ...prev, 
                          assistant: { ...prev.assistant, name: e.target.value }
                        }))}
                        placeholder={`Support Assistant - ${vectorStoreForm.name || 'Vector Store'}`}
                      />
                      <small className="form-text text-muted">
                        Leave empty to auto-generate: "Support Assistant - {vectorStoreForm.name}"
                      </small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Assistant Instructions</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={vectorStoreForm.assistant.instructions}
                        onChange={(e) => setVectorStoreForm(prev => ({ 
                          ...prev, 
                          assistant: { ...prev.assistant, instructions: e.target.value }
                        }))}
                        placeholder="You are a helpful support AI. Use the attached vector store to answer questions based only on product manuals."
                      />
                      <small className="form-text text-muted">
                        Define how the assistant should behave and respond to user queries
                      </small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">GPT Model</label>
                      <input
                        type="text"
                        className="form-control"
                        value={vectorStoreForm.assistant.model}
                        onChange={(e) => setVectorStoreForm(prev => ({ 
                          ...prev, 
                          assistant: { ...prev.assistant, model: e.target.value }
                        }))}
                        placeholder="gpt-4o"
                      />
                      <small className="form-text text-muted">
                        Enter the GPT model for your assistant (e.g., gpt-4o, gpt-4o-mini, gpt-4-turbo, gpt-4, gpt-3.5-turbo). Leave empty to use the default (gpt-4o)
                      </small>
                    </div>
                  </div>
                </div>
                
                {!editingVectorStore && (
                  <div className="alert alert-info">
                    <small>
                      <strong>Note:</strong> This will create a new OpenAI vector store with ID format: 
                      <code>extremeb2b-{accountInfo.account_id}</code> and an OpenAI assistant attached to it.
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #dee2e6', position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1000 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseVectorStoreModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={editingVectorStore ? handleUpdateVectorStore : handleCreateVectorStore}
                  disabled={creatingVectorStore || !vectorStoreForm.name.trim()}
                >
                  {creatingVectorStore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {editingVectorStore ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      {editingVectorStore ? 'Update Store' : 'Create Store'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAuthKeyModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit API Key</h5>
                <button type="button" className="btn-close" onClick={closeAuthKeyEditModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Key Name</label>
                  <input
                    className="form-control"
                    value={authKeyEditName}
                    onChange={(e) => setAuthKeyEditName(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={authKeyEditDescription}
                    onChange={(e) => setAuthKeyEditDescription(e.target.value)}
                  />
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="authKeyActiveToggle"
                    checked={authKeyEditActive}
                    onChange={(e) => setAuthKeyEditActive(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="authKeyActiveToggle">
                    Key is active
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeAuthKeyEditModal}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveAuthKeyMeta}
                  disabled={authActionLoading || !authKeyEditName.trim()}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDeleteVectorStore}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this vector store?</p>
                <p className="text-muted">
                  <strong>This action cannot be undone.</strong> All data associated with this vector store will be permanently removed.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDeleteVectorStore}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDeleteVectorStore}
                >
                  Delete Vector Store
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Configurations;
